import {
  Button,
  Carousel,
  Heading,
  Icon,
  LinkButton,
  Modal,
  SegmentGroup,
  Spinner,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { EyeClosed, InfoCircle, Key, Trash, WarningTriangle } from "iconoir-react";
import { Box, Center, Flex, Grid } from "styled-system/jsx";
import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import {
  createGoogleMapsUrl,
  DATE_PATTERN_STANDARD,
  formatDate,
  getAddressString,
  getMultilingualString,
} from "shared/utils";
import { BackOfficeCrowdRiffFlyoutSection } from "apps/directory/Operator/views/BackOffice/BackOfficeCrowdRiff";
import { AdminlandBackOfficeContent } from "apps/adminland/directories/listings/AdminlandBackOfficeContent";
import {
  BackOfficeTags,
  CloudinaryImage,
  ConfirmInputModal,
  HtmlContent,
  htmlContentProseClassName,
  InviteStaffModal,
  OperatorNotesTimeline,
  PersonDetailsModal,
  PeopleTagsContextProvider,
} from "shared/components";
import { NotesContextProvider } from "shared/contextProviders/notes";
import { ButtonCard, SmartImage } from "shared/components/atoms";
import { PeopleContextProvider, useFeatureFlag } from "shared/contextProviders";
import { FeatureFlags } from "shared/featureFlags";
import {
  AssetImage,
  EnumEntityReferenceType,
  EnumInviteQueueInviteStatus,
  OperatorByIdDocument,
  OperatorManyDirectoryDocument,
  OperatorManyDocument,
  useInviteQueueByOperatorAndOrganizationQuery,
  useInviteQueueCancelByIdMutation,
  useInviteQueueCreateOneMutation,
  useOperatorByIdQuery,
  useOperatorDeleteCompletelyByInternalMutation,
  useOperatorDeleteMutation,
  useOperatorMarkDeletedByInternalMutation,
  useOperatorUpdateMutation,
  UserWithPictureTc,
} from "gql/generated";
import type { UseInviteStaffProps } from "../InviteStaffModal/useInviteStaff";
import { HoursOfOperation } from "../OperatorDetailsSidePane/HoursOfOperation";
import { Notes } from "../Notes";
import {
  EnumOperatorOperationalStatus,
  EnumPublicationStatusStatus,
  EnumUserAccessOperatorsStatus,
  EnumUserStatus,
  EnumVersionedEntityReferenceType,
  PublicationStatusType,
  useCreatePublicationStatusMutation,
} from "gql/generated";
import { Tag } from "@ttc3k/trekker";
import { gql, useQuery } from "@apollo/client";
import _ from "lodash";
import { useAppContext } from "useAppContext";

export type OperatorProfileFlyoutProps = {
  isOpen: boolean;
  onClose: () => void;
  operatorId: string | null;
  organizationId: string | undefined;
  /** When provided, Previous/Next buttons allow browsing these operators in the flyout. */
  operatorIdsForNavigation?: string[];
  /** Called when the user selects a different operator via Prev/Next. Parent should set operatorId. */
  onOperatorIdChange?: (operatorId: string) => void;
  /** Called when user clicks Edit listing. Defaults to navigating to canonical directory URL. */
  onEditListing?: (operatorId: string) => void;
  /** Side the flyout opens on. Passed directly to the trekker Modal. */
  placement?: "leftPane" | "rightPane";
  onOperatorUpdated?: (updatedOperator: {
    _entityId?: string | null;
    status?: PublicationStatusType | null;
    claimed?: boolean | null;
    enrollments?: unknown;
    tags?: unknown;
  }) => void;
};

const VIEW_KEY = { BACK_OFFICE: "back_office", QUICK_VIEW: "quick_view", NOTES: "notes" } as const;
type ViewKeyValue = (typeof VIEW_KEY)[keyof typeof VIEW_KEY];

const PUBLICATION_STATUS_HISTORY_QUERY = gql`
  query PublicationStatusHistory($entityId: MongoID!, $organizationId: MongoID!, $limit: Int) {
    publicationStatusHistory(entityId: $entityId, organizationId: $organizationId, limit: $limit) {
      status
      changeSummary
      consolidatedChangeSummary
      updatedDateISO
      changedByName
      isStale
    }
  }
`;

type PublicationStatusHistoryRecord = {
  status: string;
  changeSummary?: string | null;
  consolidatedChangeSummary?: string | null;
  updatedDateISO?: string | null;
  changedByName?: string | null;
  isStale?: boolean | null;
};

const INVITE_QUEUE_FOR_OPERATOR_QUERY = gql`
  query InviteQueueForOperator($filter: FilterFindManyInviteQueueInput, $limit: Int) {
    inviteQueueMany(filter: $filter, limit: $limit) {
      _id
      invitedUser
      invitedUserName
      invitedEmail
      initiatedByUserName
      initiatedDate
      inviteStatus
    }
  }
`;

type InviteQueueForOperatorEntry = {
  _id: string;
  invitedUser?: string | null;
  invitedUserName?: string | null;
  invitedEmail?: string | null;
  initiatedByUserName?: string | null;
  initiatedDate?: string | null;
  inviteStatus: string;
};

/* Figma: Operating hours – zebra stripe, thin white borders between rows and columns. */
const flyoutHoursTableClass = css({
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "8px",
  overflow: "hidden",
  "& td": {
    paddingTop: "100",
    paddingBottom: "100",
    paddingLeft: "200",
    paddingRight: "200",
    verticalAlign: "middle",
    textAlign: "left",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "#ffffff",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: "#ffffff",
  },
  "& td:first-child": {
    paddingRight: "300",
  },
  "& td:last-child": {
    borderRightWidth: "0",
  },
  "& tr:last-child td": {
    borderBottomWidth: "0",
  },
  "& tr:nth-child(odd) td": {
    backgroundColor: "#f4f4f5",
  },
  "& tr:nth-child(even) td": {
    backgroundColor: "#fafafa",
  },
});

/* Figma: Listing details – white card with drop shadow, rounded corners, thin dividers */
const listingDetailsCardClass = css({
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#d4d4d8",
  boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.10)",
  overflow: "hidden",
  "& > * + *": {
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "#d4d4d8",
    paddingTop: "200",
  },
  "& > *": {
    paddingBottom: "200",
    paddingLeft: "300",
    paddingRight: "300",
  },
  "& > *:first-child": {
    paddingTop: "200",
  },
});

const flyoutQuickViewErrorValueClass = css({
  color: "text.error.dark",
});

/* Staff table – match operating hours: zebra stripe, thin white borders between rows and columns */
const staffTableClass = css({
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "100",
  "& th, & td": {
    paddingTop: "100",
    paddingBottom: "100",
    paddingLeft: "200",
    paddingRight: "200",
    verticalAlign: "middle",
    textAlign: "left",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "#ffffff",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: "#ffffff",
  },
  "& th:first-child, & td:first-child": {
    paddingRight: "300",
  },
  "& th:last-child, & td:last-child": {
    borderRightWidth: "0",
  },
  "& tr:last-child th, & tr:last-child td": {
    borderBottomWidth: "0",
  },
  "& th": {
    backgroundColor: "#f4f4f5",
    fontWeight: 600,
  },
  "& tbody tr:nth-child(odd) td": {
    backgroundColor: "#f4f4f5",
  },
  "& tbody tr:nth-child(even) td": {
    backgroundColor: "#fafafa",
  },
});

/* Tag colour styles – applied via `style` so the Trekker Tag recipe (padding, borderRadius) is preserved. */
const tagBusinessStyle = { backgroundColor: "#26a69a", color: "#ffffff" } as const;
const tagManagementStyle = { backgroundColor: "#2383e2", color: "#ffffff" } as const;
const tagOperationalOpenStyle = { backgroundColor: "#ea580c", color: "#ffffff" } as const;
const tagOperationalTempClosedStyle = { backgroundColor: "#c2410c", color: "#ffffff" } as const;
const tagOperationalPermClosedStyle = { backgroundColor: "#dc2626", color: "#ffffff" } as const;
const tagKeywordStyle = { backgroundColor: "#c5eae6", color: "#117c72" } as const;
const tagLastUpdatedStyle = { backgroundColor: "#000000", color: "#ffffff" } as const;

function phoneToTelHref(phone: string) {
  const digits = phone.replace(/[^\d+]/g, "");
  return digits.length > 0 ? `tel:${digits}` : `tel:${encodeURIComponent(phone.trim())}`;
}

function getOperationalStatusTagStyle(
  status: typeof EnumOperatorOperationalStatus[keyof typeof EnumOperatorOperationalStatus],
) {
  if (status === EnumOperatorOperationalStatus.PermanentlyClosed)
    return tagOperationalPermClosedStyle;
  if (status === EnumOperatorOperationalStatus.TemporarilyClosed)
    return tagOperationalTempClosedStyle;
  return tagOperationalOpenStyle;
}

function getNeedsAttentionUpdatedBannerKey(
  consolidatedSummary: string | null,
  summariesLength: number,
  changedByName: string | null | undefined,
) {
  const useSummaryLine =
    Boolean(consolidatedSummary) || summariesLength <= 2;
  const hasActor = Boolean(changedByName?.trim());
  if (hasActor) {
    return useSummaryLine
      ? "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_UPDATED"
      : "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_UPDATED_HEADER";
  }
  return useSummaryLine
    ? "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_UPDATED_ANONYMOUS"
    : "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_UPDATED_HEADER_ANONYMOUS";
}

export const OperatorProfileFlyout = ({
  isOpen,
  onClose,
  operatorId,
  organizationId,
  operatorIdsForNavigation = [],
  onOperatorIdChange,
  onEditListing,
  placement,
  onOperatorUpdated,
}: OperatorProfileFlyoutProps) => {
  const { t } = useTranslation();
  const { t: tCore } = useTranslation("core");
  const { t: tAdminlandListing } = useTranslation("adminland", {
    keyPrefix: "PAGE.LISTINGS.ADMINLAND_BACKOFFICE",
  });
  const isDirectoryV2 = useFeatureFlag(FeatureFlags.DirectoryV2);
  const enableDirectorySlideout = useFeatureFlag(FeatureFlags.DirectorySlideout);
  const showNotesTimeline = isDirectoryV2 || enableDirectorySlideout;
  const { organizations } = useAppContext();
  const currentOrg = organizations?.find(o => o._id === organizationId);
  const orgShortName = (currentOrg as { shortName?: string } | undefined)?.shortName ?? "";
  const operatorOnboardingDisabled =
    currentOrg?.metadata?.operatorOnboardingDisabled ?? false;
  const [selectedView, setSelectedView] = useState<ViewKeyValue>(
    VIEW_KEY.BACK_OFFICE,
  );

  const { data, loading, error, refetch: refetchOperator } = useOperatorByIdQuery({
    variables: { operatorId: operatorId ?? "" },
    skip: !operatorId,
  });

  const operator = data?.operatorById;

  const crowdriffEnabled = (operator?.enrollments?.find(
    e => e?.organization?._id === organizationId,
  )?.organization?.metadata as { crowdriffEnabled?: boolean } | undefined)?.crowdriffEnabled;

  const isNeedsApproval = operator?.status === PublicationStatusType.NeedsApproval;
  const { data: pubStatusData } = useQuery<{
    publicationStatusHistory: PublicationStatusHistoryRecord[];
  }>(PUBLICATION_STATUS_HISTORY_QUERY, {
    variables: {
      entityId: operator?._entityId,
      organizationId,
      limit: 20,
    },
    skip: !isNeedsApproval || !operator?._entityId || !organizationId,
  });

  const needsAttentionBanner = useMemo(() => {
    if (!isNeedsApproval || !pubStatusData?.publicationStatusHistory?.length) return null;
    const records = pubStatusData.publicationStatusHistory;
    const pendingRecords: PublicationStatusHistoryRecord[] = [];
    for (const r of records) {
      if (r.status !== "NEEDS_APPROVAL") break;
      pendingRecords.push(r);
    }
    if (pendingRecords.length === 0) return null;

    const summaries = pendingRecords
      .map(r => r.changeSummary)
      .filter((s): s is string => !!s && s !== "Not available" && s !== "No meaningful changes.");
    const consolidatedSummary = pendingRecords[0]?.consolidatedChangeSummary ?? null;
    const date = pendingRecords[pendingRecords.length - 1]?.updatedDateISO;
    const changedByName =
      pendingRecords.find(r => r.changedByName)?.changedByName ?? null;
    const isStale = pendingRecords.some(r => r.isStale);

    return { summaries, consolidatedSummary, date, changedByName, isStale };
  }, [isNeedsApproval, pubStatusData]);

  useEffect(() => {
    if (isOpen) setSelectedView(VIEW_KEY.BACK_OFFICE);
  }, [isOpen, operatorId]);
  useEffect(() => {
    setShowTagsExplanation(false);
  }, [operatorId]);
  useEffect(() => {
    setUpkeepStrategy(null);
    setSelectedStaffIds(new Set());
    deferUpkeepSkipEnrollmentInferenceRef.current = false;
  }, [operatorId]);

  // Freeze navigation list when flyout opens so Prev/Next keep working after approving
  // (approved listings are removed from the parent's list, but we preserve the original context)
  const frozenNavIdsRef = useRef<string[]>([]);
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current && operatorIdsForNavigation.length > 0) {
      frozenNavIdsRef.current = operatorIdsForNavigation;
      wasOpenRef.current = true;
    }
    if (!isOpen) {
      wasOpenRef.current = false;
      frozenNavIdsRef.current = [];
    }
  }, [isOpen, operatorIdsForNavigation]);
  const navIds =
    frozenNavIdsRef.current.length > 0
      ? frozenNavIdsRef.current
      : operatorIdsForNavigation;

  const currentIndex = operatorId ? navIds.indexOf(operatorId) : -1;
  const hasPrev = navIds.length > 0 && currentIndex > 0;
  const hasNext =
    navIds.length > 0 &&
    currentIndex >= 0 &&
    currentIndex < navIds.length - 1;
  const goPrev = useCallback(() => {
    if (!hasPrev) return;
    const prevId = navIds[currentIndex - 1];
    if (prevId) onOperatorIdChange?.(prevId);
  }, [hasPrev, currentIndex, navIds, onOperatorIdChange]);
  const goNext = useCallback(() => {
    if (!hasNext) return;
    const nextId = navIds[currentIndex + 1];
    if (nextId) onOperatorIdChange?.(nextId);
  }, [hasNext, currentIndex, navIds, onOperatorIdChange]);

  const handleEditListing = useCallback(() => {
    if (!operator?._entityId) return;
    if (onEditListing) {
      onEditListing(operator._entityId);
    } else if (organizationId) {
      window.open(
        `/app/organization/${organizationId}/directory/${operator._entityId}`,
        "_blank",
      );
    }
  }, [operator?._entityId, organizationId, onEditListing]);

  const [imageWidth, setImageWidth] = useState(0);
  const [showTagsExplanation, setShowTagsExplanation] = useState(false);
  const [upkeepStrategy, setUpkeepStrategy] = useState<
    null | "myOrganization" | "businessOwner"
  >(null);
  /** After "Defer" (move to directory without upkeep choice), skip inferring strategy from enrollments/queue. */
  const deferUpkeepSkipEnrollmentInferenceRef = useRef(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [isApprovingListing, setIsApprovingListing] = useState(false);
  const [approveListingSuccess, setApproveListingSuccess] = useState(false);
  const [isUndoingDmoManaged, setIsUndoingDmoManaged] = useState(false);
  const [addingAllToQueue, setAddingAllToQueue] = useState(false);
  const [isInviteStaffModalOpen, setIsInviteStaffModalOpen] = useState(false);
  const [selectedInviteStaffUser, setSelectedInviteStaffUser] = useState<
    Partial<UserWithPictureTc> | undefined
  >(undefined);
  const [isPersonDetailsModalOpen, setIsPersonDetailsModalOpen] = useState(false);
  const [personDetailsId, setPersonDetailsId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { toastFactory } = useToastContext();
  const [updateOperator] = useOperatorUpdateMutation({
    refetchQueries: [OperatorByIdDocument, OperatorManyDirectoryDocument],
  });
  const [bannerExpanded, setBannerExpanded] = useState(false);
  const bannerTextRef = useRef<HTMLDivElement>(null);
  const [bannerOverflows, setBannerOverflows] = useState(false);
  const [createPublicationStatus, { loading: isFlaggingNeedsAttention }] =
    useCreatePublicationStatusMutation({
      refetchQueries: [OperatorByIdDocument, OperatorManyDirectoryDocument],
    });

  // Danger Zone: archive/delete
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isMarkDeletedModalOpen, setIsMarkDeletedModalOpen] = useState(false);
  const [isDeleteCompletelyModalOpen, setIsDeleteCompletelyModalOpen] =
    useState(false);
  const [archivePublicationStatus, { loading: loadingArchive }] =
    useCreatePublicationStatusMutation({
      refetchQueries: [OperatorByIdDocument, OperatorManyDocument],
    });
  const [deleteOperator, { loading: loadingDelete }] =
    useOperatorDeleteMutation({
      refetchQueries: [OperatorManyDocument],
    });
  const [markDeletedByInternal, { loading: loadingMarkDeletedByInternal }] =
    useOperatorMarkDeletedByInternalMutation({
      refetchQueries: [
        OperatorByIdDocument,
        OperatorManyDocument,
        OperatorManyDirectoryDocument,
      ],
    });
  const [
    deleteCompletelyByInternal,
    { loading: loadingDeleteCompletelyByInternal },
  ] = useOperatorDeleteCompletelyByInternalMutation({
    refetchQueries: [
      OperatorByIdDocument,
      OperatorManyDocument,
      OperatorManyDirectoryDocument,
    ],
  });

  const isUnpublished = operator?.status === PublicationStatusType.Unpublished;

  const handleArchiveOrRepublish = async (shouldUnpublish: boolean) => {
    if (!operator?._entityId || !organizationId) return;
    try {
      await archivePublicationStatus({
        variables: {
          record: {
            entity: {
              _entityId: String(operator._entityId),
              _versionId: String(operator._versionId),
              type: EnumVersionedEntityReferenceType.Operator,
            },
            organization: organizationId,
            status: shouldUnpublish
              ? EnumPublicationStatusStatus.Unpublished
              : EnumPublicationStatusStatus.NeedsApproval,
          },
        },
      });
      setIsArchiveModalOpen(false);
    } catch {
      /* error handled by Apollo */
    }
  };

  const handleDeleteOperator = async () => {
    if (!operator?._entityId) return;
    try {
      await deleteOperator({
        variables: { entityId: String(operator._entityId) },
      });
      setIsDeleteModalOpen(false);
      onOperatorUpdated?.({
        _entityId: String(operator._entityId),
        status: PublicationStatusType.Deleted,
        enrollments: operator.enrollments?.filter(
          e => e?.organization?._id !== organizationId,
        ) ?? [],
      });
      onClose();
    } catch {
      /* error handled by Apollo */
    }
  };

  const handleMarkDeletedByInternal = async () => {
    if (!operator?._entityId) return;
    try {
      await markDeletedByInternal({
        variables: { entityId: String(operator._entityId) },
      });
      setIsMarkDeletedModalOpen(false);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: tAdminlandListing("MARK_DELETED_SUCCESS"),
      });
      onOperatorUpdated?.({
        _entityId: String(operator._entityId),
        enrollments: [],
        status: PublicationStatusType.Deleted,
      });
      refetchOperator();
    } catch (err) {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description:
          err instanceof Error
            ? err.message
            : tAdminlandListing("MARK_DELETED_ERROR"),
      });
    }
  };

  const handleDeleteCompletelyByInternal = async () => {
    if (!operator?._entityId) return;
    try {
      await deleteCompletelyByInternal({
        variables: { entityId: String(operator._entityId) },
      });
      setIsDeleteCompletelyModalOpen(false);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: tAdminlandListing("DELETE_COMPLETELY_SUCCESS"),
      });
      onClose();
      onOperatorUpdated?.({ _entityId: String(operator._entityId) });
    } catch (err) {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description:
          err instanceof Error
            ? err.message
            : tAdminlandListing("DELETE_COMPLETELY_ERROR"),
      });
    }
  };

  useEffect(() => {
    setApproveListingSuccess(false);
    setBannerExpanded(false);
  }, [operatorId, operator?._entityId]);

  useLayoutEffect(() => {
    if (!needsAttentionBanner || bannerExpanded) {
      setBannerOverflows(false);
      return;
    }
    const el = bannerTextRef.current;
    if (!el) return;
    setBannerOverflows(el.scrollHeight > el.clientHeight);
  }, [needsAttentionBanner, bannerExpanded]);
  const handleApproveListingClick = useCallback(async () => {
    if (!operator?._entityId || !organizationId || !operator.enrollments?.length) return;
    setIsApprovingListing(true);
    const isDraft = !operator.status || operator.status === PublicationStatusType.Draft;
    const anotherOrgManaging = operator.enrollments.some(
      e => e?.organization?._id && String(e.organization._id) !== String(organizationId) && e.organizationControlled === true,
    );
    const updatedEnrollments = operator.enrollments
      .filter((e): e is NonNullable<typeof e> & { organization: { _id: unknown } } => Boolean(e?.organization?._id))
      .map(e => {
        const isCurrentOrg = String(e.organization._id) === String(organizationId);
        return {
          ...e,
          organizationControlled: isCurrentOrg
            ? (anotherOrgManaging ? e.organizationControlled : true)
            : e.organizationControlled,
        };
      });
    try {
      await updateOperator({
        variables: {
          entityId: String(operator._entityId),
          isApproving: isDraft ? true : undefined,
          record: {
            enrollments: updatedEnrollments.map(e => ({
              organization: e.organization._id,
              organizationControlled: e.organizationControlled,
              onboardingStatus: e.onboardingStatus,
            })),
          },
        },
      });
      onOperatorUpdated?.({
        _entityId: String(operator._entityId),
        status: isDraft ? PublicationStatusType.Approved : operator.status,
        enrollments: updatedEnrollments,
      });
      setApproveListingSuccess(true);
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    } finally {
      setIsApprovingListing(false);
    }
  }, [operator, organizationId, onOperatorUpdated, updateOperator, toastFactory, t]);

  /** Approve a New (draft) listing into the directory without choosing DMO vs. business-owner upkeep. */
  const handleMoveToDirectoryDeferUpkeep = useCallback(async () => {
    if (!operator?._entityId || !organizationId || !operator.enrollments?.length) return;
    setIsApprovingListing(true);
    const isDraft =
      !operator.status || operator.status === PublicationStatusType.Draft;
    const enrollmentsPayload = operator.enrollments
      .filter(
        (e): e is NonNullable<typeof e> & { organization: { _id: unknown } } =>
          Boolean(e?.organization?._id),
      )
      .map(e => ({
        organization: e.organization._id,
        organizationControlled: e.organizationControlled,
        onboardingStatus: e.onboardingStatus,
      }));
    try {
      await updateOperator({
        variables: {
          entityId: String(operator._entityId),
          isApproving: isDraft ? true : undefined,
          record: {
            enrollments: enrollmentsPayload,
          },
        },
      });
      deferUpkeepSkipEnrollmentInferenceRef.current = true;
      onOperatorUpdated?.({
        _entityId: String(operator._entityId),
        status: isDraft ? PublicationStatusType.Approved : operator.status,
        enrollments: operator.enrollments,
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    } finally {
      setIsApprovingListing(false);
    }
  }, [operator, organizationId, onOperatorUpdated, updateOperator, toastFactory, t]);

  const handleUndoDmoManaged = useCallback(async () => {
    if (!operator?._entityId || !organizationId || !operator.enrollments?.length) return;
    setIsUndoingDmoManaged(true);
    const updatedEnrollments = operator.enrollments
      .filter((e): e is NonNullable<typeof e> & { organization: { _id: unknown } } => Boolean(e?.organization?._id))
      .map(e => ({
        ...e,
        organizationControlled: String(e.organization._id) === String(organizationId)
          ? false
          : e.organizationControlled,
      }));
    try {
      await updateOperator({
        variables: {
          entityId: String(operator._entityId),
          record: {
            enrollments: updatedEnrollments.map(e => ({
              organization: e.organization._id,
              organizationControlled: e.organizationControlled,
              onboardingStatus: e.onboardingStatus,
            })),
          },
        },
      });
      onOperatorUpdated?.({
        _entityId: String(operator._entityId),
        enrollments: updatedEnrollments,
      });
      await refetchOperator();
      setUpkeepStrategy(null);
      deferUpkeepSkipEnrollmentInferenceRef.current = false;
      setApproveListingSuccess(false);
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
      });
    } finally {
      setIsUndoingDmoManaged(false);
    }
  }, [operator, organizationId, onOperatorUpdated, updateOperator, refetchOperator, toastFactory, t]);

  /** Keeps Directory v2 in sync when the active operatorMany query is skipped (merged local cache). */
  const notifyDirectoryOperatorApproved = useCallback(() => {
    if (!operator?._entityId) return;
    onOperatorUpdated?.({
      _entityId: String(operator._entityId),
      status: PublicationStatusType.Approved,
    });
  }, [operator?._entityId, onOperatorUpdated]);

  const operatorEntityIdStr = operator?._entityId != null ? String(operator._entityId) : "";
  const organizationIdStr = organizationId != null ? String(organizationId) : "";
  const {
    data: inviteQueueData,
    refetch: refetchInviteQueue,
  } = useInviteQueueByOperatorAndOrganizationQuery({
    variables: { operatorEntityId: operatorEntityIdStr, organizationId: organizationIdStr },
    skip: !operatorEntityIdStr || !organizationIdStr,
  });
  const inviteQueueEntry = inviteQueueData?.inviteQueueByOperatorAndOrganization;
  const [addToInviteQueue, { loading: addingToInviteQueue }] = useInviteQueueCreateOneMutation({
    onCompleted: () => {
      refetchInviteQueue();
      if (operator?._entityId && operator?._versionId && operator.status !== PublicationStatusType.Approved) {
        void createPublicationStatus({
          variables: {
            record: {
              entity: {
                _entityId: String(operator._entityId),
                _versionId: String(operator._versionId),
                type: EnumVersionedEntityReferenceType.Operator,
              },
              organization: organizationId,
              status: EnumPublicationStatusStatus.Approved,
            },
          },
        }).then(() => {
          notifyDirectoryOperatorApproved();
        });
      }
    },
    refetchQueries: ["InviteQueueMany", "InviteQueueForOperator"],
  });
  const [cancelInviteQueue, { loading: cancellingInviteQueue }] = useInviteQueueCancelByIdMutation({
    onCompleted: () => refetchInviteQueue(),
    refetchQueries: ["InviteQueueMany", "InviteQueueForOperator"],
  });

  const {
    data: allInviteQueueData,
    refetch: refetchAllInviteQueue,
  } = useQuery<{
    inviteQueueMany: InviteQueueForOperatorEntry[];
  }>(INVITE_QUEUE_FOR_OPERATOR_QUERY, {
    variables: {
      filter: { invitedOperator: operatorEntityIdStr, organizationId: organizationIdStr },
      limit: 100,
    },
    skip: !operatorEntityIdStr || !organizationIdStr,
  });
  const {
    data: operatorInviteQueueData,
    refetch: refetchOperatorInviteQueue,
  } = useQuery<{
    inviteQueueMany: InviteQueueForOperatorEntry[];
  }>(INVITE_QUEUE_FOR_OPERATOR_QUERY, {
    variables: {
      filter: { invitedOperator: operatorEntityIdStr },
      limit: 100,
    },
    skip: !operatorEntityIdStr,
  });

  const refreshInviteStaffState = useCallback(async () => {
    await Promise.all([
      refetchOperator(),
      refetchInviteQueue(),
      refetchAllInviteQueue(),
      refetchOperatorInviteQueue(),
    ]);
  }, [
    refetchAllInviteQueue,
    refetchInviteQueue,
    refetchOperator,
    refetchOperatorInviteQueue,
  ]);

  const handleInviteStaffModalClose = useCallback(() => {
    setIsInviteStaffModalOpen(false);
    setSelectedInviteStaffUser(undefined);
    void refreshInviteStaffState();
  }, [refreshInviteStaffState]);

  const activeInviteEntries = useMemo(
    () =>
      (allInviteQueueData?.inviteQueueMany ?? []).filter(
        e =>
          e.inviteStatus !== EnumInviteQueueInviteStatus.Cancelled,
      ),
    [allInviteQueueData],
  );
  const activeOperatorInviteEntries = useMemo(
    () =>
      (operatorInviteQueueData?.inviteQueueMany ?? []).filter(
        entry => entry.inviteStatus !== EnumInviteQueueInviteStatus.Cancelled,
      ),
    [operatorInviteQueueData],
  );

  const invitedUserIds = useMemo(
    () => new Set(activeInviteEntries.map(e => e.invitedUser ? String(e.invitedUser) : null).filter(Boolean) as string[]),
    [activeInviteEntries],
  );

  useEffect(() => {
    if (upkeepStrategy !== null) return;
    if (deferUpkeepSkipEnrollmentInferenceRef.current) return;
    const currentEnrollment = operator?.enrollments?.find(
      e => e?.organization?._id === organizationId,
    );
    const otherOrgManaging = operator?.enrollments?.some(
      e =>
        e?.organization?._id !== organizationId &&
        e?.organizationControlled === true,
    );
    if (
      operatorOnboardingDisabled ||
      currentEnrollment?.organizationControlled === true ||
      otherOrgManaging
    ) {
      setUpkeepStrategy("myOrganization");
    } else if (inviteQueueEntry != null) {
      setUpkeepStrategy("businessOwner");
    }
  }, [
    inviteQueueEntry,
    operator?.enrollments,
    organizationId,
    upkeepStrategy,
    operatorOnboardingDisabled,
  ]);

  /** Draft "New" banner: claimed and DMO-managed listings do not use the strategy selector but still have an upkeep choice. */
  const listingUpkeepStrategySelected = useMemo(() => {
    if (upkeepStrategy != null) return true;
    if (!operator) return false;
    const currentEnrollment = operator.enrollments?.find(
      e => e?.organization?._id === organizationId,
    );
    if (currentEnrollment?.organizationControlled === true) return true;
    return (operator as { claimed?: boolean }).claimed === true;
  }, [operator, organizationId, upkeepStrategy]);

  const checkScrollWidth = useCallback(() => {
    if (contentRef.current) {
      setImageWidth(contentRef.current.scrollWidth);
    } else {
      setTimeout(() => checkScrollWidth(), 1);
    }
  }, []);
  useEffect(() => {
    setImageWidth(0);
    if (operator) setTimeout(() => checkScrollWidth(), 1);
  }, [operator, checkScrollWidth, selectedView]);

  const images = useMemo(
    () => (operator?.images ?? []) as Partial<AssetImage>[],
    [operator?.images],
  );
  const flyoutGallerySection = useMemo(
    () =>
      images.length > 0 && imageWidth > 0 ? (
        <Box position="relative">
          {images.length === 1 ? (
            <Flex
              alignItems="center"
              justifyContent="center"
              h="400px"
              pb="150"
            >
              <SmartImage
                image={images[0]}
                width={imageWidth || 465}
                height={400}
                shape="square"
                previewVideo={true}
              />
            </Flex>
          ) : (
            <Carousel
              images={images}
              controlClassName={css({ pb: "150" })}
              tagClassName={css({ pb: "150" })}
              render={(image: Partial<AssetImage>) => (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  h="400px"
                >
                  <SmartImage
                    image={image}
                    width={imageWidth || 465}
                    height={400}
                    shape="square"
                    previewVideo={true}
                  />
                </Flex>
              )}
            />
          )}
        </Box>
      ) : (
        <CloudinaryImage
          imageStyleProps={{
            h: "400px",
            borderStyle: "none",
            borderRadius: "0",
            aspectRatio: `${Math.max(imageWidth, 1)} : 400`,
            width: "full",
          }}
        />
      ),
    [images, imageWidth],
  );
  const hasValidHours =
    operator?.hoursOfOperation &&
    !_.isEmpty(operator.hoursOfOperation) &&
    Object.keys(operator.hoursOfOperation).filter(k => k !== "__typename")
      .length > 0;

  const open = isOpen;

  return (
    <>
    <Modal
      open={open}
      onOpenChange={({ open: o }: { open: boolean }) => !o && onClose()}
      closeTriggerVisual="light"
      closeTriggerPlacement="booker"
      onExitComplete={onClose}
      zIndex="max"
      {...(placement ? { placement } : {})}
    >
      {!operatorId ? null : loading ? (
        <Center py="600">
          <Spinner width="40px" height="40px" />
        </Center>
      ) : error ? (
        <Heading mx="400" my="600">
          {t("directory:OPERATOR.FLYOUT.ERROR_FETCH")}
        </Heading>
      ) : !operator ? null : (
        <Grid
          ref={contentRef}
          bg="white"
          h="100%"
          gridTemplateRows="1fr auto"
          overflow="hidden"
          minH="0"
        >
          {/* Single scroll; gallery is inline on Quick View only (below tags). Sticky tab header. */}
          <Flex
            flexDir="column"
            minH="0"
            overflow="auto"
            flex={1}
          >
            <Box
              position="relative"
              zIndex={10}
              bg="white"
              display="flex"
              flexDir="column"
              style={{ transform: "translateZ(0)" }}
            >
              {/* Operator name + explainer tags + tab selector – sticky header */}
              <Flex
                position="sticky"
                top={0}
                flexDir="column"
                px="400"
                pt="600"
                pb="400"
                bg="white"
                borderBottomWidth="1px"
                borderColor="border.light"
                flexShrink={0}
                zIndex={2}
                gap="200"
              >
                <Heading size="h3">
                  {getMultilingualString(operator.name)}
                </Heading>
                <Box width="100%" fontSize="xs" lineHeight="lg" color="text.mid">
                  <Button
                    variant="link"
                    mode="light"
                    size="sm"
                    aria-label={t("directory:OPERATOR.FLYOUT.TAGS_INFO_ICON_ARIA")}
                    aria-pressed={showTagsExplanation}
                    onClick={() =>
                      setShowTagsExplanation(prev => !prev)
                    }
                    display="inline-flex"
                    flexShrink={0}
                    alignItems="center"
                    justifyContent="center"
                    verticalAlign="middle"
                    mb="50"
                    mr="100"
                    className={css({
                      boxSizing: "border-box",
                      padding: "4px 8px",
                      borderRadius: "8px",
                      minHeight: "unset",
                      height: "28px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      borderColor: "#d4d4d8",
                    })}
                  >
                    <Icon
                      Element={InfoCircle}
                      w="18px"
                      h="18px"
                      color="icon.mid"
                    />
                  </Button>
                  {operator.type && (
                    <>
                      <Tag
                        size="sm"
                        style={{ ...tagBusinessStyle, display: "inline-flex", verticalAlign: "middle" }}
                      >
                        {t(`core:LOOKUPS.OPERATOR_TYPE.${operator.type}`)}
                      </Tag>{" "}
                      <AnimatePresence>
                        {showTagsExplanation && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ display: "inline" }}
                          >
                            <Text as="span" visual="smallRegular" color="text.mid">
                              {t(
                                "directory:OPERATOR.FLYOUT.TAGS_INFO_LISTING_TYPE_SUFFIX",
                              )}
                            </Text>
                          </motion.span>
                        )}
                      </AnimatePresence>{" "}
                    </>
                  )}
                  {(operator as { claimed?: boolean }).claimed && (
                    <>
                      <Tag
                        size="sm"
                        style={{ ...tagManagementStyle, display: "inline-flex", verticalAlign: "middle" }}
                      >
                        {t("directory:OPERATOR.FLYOUT.TAG_CLAIMED")}
                      </Tag>{" "}
                      <AnimatePresence>
                        {showTagsExplanation && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ display: "inline" }}
                          >
                            <Text as="span" visual="smallRegular" color="text.mid">
                              {t(
                                "directory:OPERATOR.FLYOUT.TAGS_INFO_CLAIMED_SUFFIX",
                              )}
                            </Text>
                          </motion.span>
                        )}
                      </AnimatePresence>{" "}
                    </>
                  )}
                  {(() => {
                    const currentEnrollment = operator.enrollments?.find(
                      e => e?.organization?._id === organizationId,
                    );
                    const isOrgControlled =
                      currentEnrollment?.organizationControlled === true;
                    if (!isOrgControlled) return null;
                    return (
                      <>
                        <Tag
                          size="sm"
                          style={{
                            ...tagManagementStyle,
                            display: "inline-flex",
                            verticalAlign: "middle",
                          }}
                        >
                          {`${orgShortName || "DMO"} Managed`}
                        </Tag>{" "}
                        <AnimatePresence>
                          {showTagsExplanation && (
                            <motion.span
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              style={{ display: "inline" }}
                            >
                              <Text
                                as="span"
                                visual="smallRegular"
                                color="text.mid"
                              >
                                {(operator as { claimed?: boolean }).claimed
                                  ? t(
                                      "directory:OPERATOR.FLYOUT.TAGS_INFO_MANAGED_BY_DMO_ALSO_SUFFIX",
                                    )
                                  : t(
                                      "directory:OPERATOR.FLYOUT.TAGS_INFO_MANAGED_BY_DMO_SUFFIX",
                                    )}
                              </Text>
                            </motion.span>
                          )}
                        </AnimatePresence>{" "}
                      </>
                    );
                  })()}
                  {operator.operationalStatus && (
                    <>
                      <Tag
                        size="sm"
                        style={{
                          ...getOperationalStatusTagStyle(operator.operationalStatus),
                          display: "inline-flex",
                          verticalAlign: "middle",
                        }}
                      >
                        {operator.operationalStatus ===
                        EnumOperatorOperationalStatus.Open
                          ? t("directory:OPERATOR.FLYOUT.TAG_OPERATIONAL")
                          : t(
                              `core:LOOKUPS.OPERATOR_OPERATIONAL_STATUS.${operator.operationalStatus}`,
                            )}
                      </Tag>{" "}
                      <AnimatePresence>
                        {showTagsExplanation && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ display: "inline" }}
                          >
                            <Text as="span" visual="smallRegular" color="text.mid">
                              {operator.operationalStatus ===
                              EnumOperatorOperationalStatus.Open
                                ? t(
                                    "directory:OPERATOR.FLYOUT.TAGS_INFO_OPERATIONAL_OPEN_SUFFIX",
                                  )
                                : operator.operationalStatus ===
                                    EnumOperatorOperationalStatus.TemporarilyClosed
                                  ? t(
                                      "directory:OPERATOR.FLYOUT.TAGS_INFO_OPERATIONAL_TEMP_CLOSED_SUFFIX",
                                      {
                                        date:
                                          operator.updatedDateISO != null
                                            ? formatDate(
                                                operator.updatedDateISO,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t(
                                                "directory:OPERATOR.FLYOUT.TAGS_INFO_DATE_NOT_AVAILABLE",
                                              ),
                                      },
                                    )
                                  : t(
                                      "directory:OPERATOR.FLYOUT.TAGS_INFO_OPERATIONAL_PERM_CLOSED_SUFFIX",
                                      {
                                        date:
                                          operator.updatedDateISO != null
                                            ? formatDate(
                                                operator.updatedDateISO,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t(
                                                "directory:OPERATOR.FLYOUT.TAGS_INFO_DATE_NOT_AVAILABLE",
                                              ),
                                      },
                                    )}
                            </Text>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                  {operator.updatedDateISO && (
                    <>
                      {" "}
                      <Tag
                        size="sm"
                        style={{
                          ...tagLastUpdatedStyle,
                          display: "inline-flex",
                          verticalAlign: "middle",
                        }}
                      >
                        {t(
                          "directory:OPERATOR.FLYOUT.TAG_LAST_UPDATED",
                          {
                            date: formatDate(
                              operator.updatedDateISO,
                              DATE_PATTERN_STANDARD,
                            ),
                          },
                        )}
                      </Tag>{" "}
                      <AnimatePresence>
                        {showTagsExplanation && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ display: "inline" }}
                          >
                            <Text as="span" visual="smallRegular" color="text.mid">
                              {t(
                                "directory:OPERATOR.FLYOUT.TAGS_INFO_LAST_UPDATED_SUFFIX",
                                {
                                  relativeTime: formatDistanceToNow(
                                    new Date(operator.updatedDateISO),
                                    { addSuffix: false },
                                  ),
                                },
                              )}
                            </Text>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </Box>
                <SegmentGroup.Root
                  segmentStyle="buttons"
                  orientation="horizontal"
                  value={selectedView}
                  onValueChange={v =>
                    setSelectedView((v?.value as ViewKeyValue) ?? VIEW_KEY.BACK_OFFICE)
                  }
                >
                  <SegmentGroup.Item value={VIEW_KEY.BACK_OFFICE}>
                    <Flex alignItems="center" gap="100">
                      <Icon Element={Key} w="14px" h="14px" />
                      {t("directory:OPERATOR.FLYOUT.BACK_OFFICE")}
                    </Flex>
                  </SegmentGroup.Item>
                  <SegmentGroup.Item value={VIEW_KEY.QUICK_VIEW}>
                    {t("directory:OPERATOR.FLYOUT.QUICK_VIEW")}
                  </SegmentGroup.Item>
                  <SegmentGroup.Item value={VIEW_KEY.NOTES}>
                    {t("directory:OPERATOR.FLYOUT.NOTES")}
                  </SegmentGroup.Item>
                </SegmentGroup.Root>
              </Flex>

            {/* ── Notes Tab ── */}
            {selectedView === VIEW_KEY.NOTES && (
              <Flex flexDir="column" p="400" bg="white">
                {showNotesTimeline ? (
                  <NotesContextProvider
                    defaultEntityId={operator._entityId}
                    defaultEntityType={EnumEntityReferenceType.Operator}
                    waitForDefaultEntity
                    includeArchivedNotes
                    allowArchiving
                    allowDeleting
                  >
                    <OperatorNotesTimeline
                      operatorEntityId={operator._entityId}
                      organizationId={organizationId}
                      suggestionsPortalHost={typeof document !== "undefined" ? document.body : null}
                    />
                  </NotesContextProvider>
                ) : (
                  <Notes
                    _entityId={operator._entityId}
                    type={EnumEntityReferenceType.Operator}
                    viewType="timeline"
                    suggestionsPortalHost={typeof document !== "undefined" ? document.body : null}
                  />
                )}
              </Flex>
            )}

            {/* ── Back Office Tab ── */}
            {selectedView === VIEW_KEY.BACK_OFFICE && !organizationId && operator && (
              <AdminlandBackOfficeContent
                operator={operator}
                operatorEntityId={operatorId ?? ""}
                onOperatorUpdated={onOperatorUpdated}
                refetchOperator={refetchOperator}
                onMarkDeletedClick={() => setIsMarkDeletedModalOpen(true)}
                onDeleteCompletelyClick={() =>
                  setIsDeleteCompletelyModalOpen(true)
                }
              />
            )}
            {selectedView === VIEW_KEY.BACK_OFFICE && !!organizationId && (
              <Flex
                flexDir="column"
                p="600"
                bg="white"
                gap="500"
              >
              {needsAttentionBanner && (
                <Flex
                  flexDir="column"
                  gap="200"
                  border="2px solid"
                  borderColor="border.highlight"
                  borderRadius="100"
                  bgColor="bg.highlight.lightest"
                  py="150"
                  px="200"
                >
                  <Flex flexDir="row" gap="200" alignItems="flex-start">
                    <Icon
                      Element={WarningTriangle}
                      w="24px"
                      h="24px"
                      color="icon.highlight.dark"
                      flexShrink={0}
                    />
                    <Flex flexDir="column" flex={1} minW={0} gap="100">
                      <Box
                        ref={bannerTextRef}
                        overflow="hidden"
                        style={
                          bannerExpanded
                            ? undefined
                            : {
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical" as const,
                              }
                        }
                      >
                        {needsAttentionBanner.isStale
                          ? <Text visual="bodyRegular" color="text.highlight.dark">
                              {needsAttentionBanner.summaries.join(" ")}
                            </Text>
                          : needsAttentionBanner.summaries.length > 0
                            ? <Box>
                                <Text visual="bodyRegular" color="text.highlight.dark">
                                  {t(
                                    getNeedsAttentionUpdatedBannerKey(
                                      needsAttentionBanner.consolidatedSummary,
                                      needsAttentionBanner.summaries.length,
                                      needsAttentionBanner.changedByName,
                                    ),
                                    needsAttentionBanner.changedByName?.trim()
                                      ? {
                                          name: needsAttentionBanner.changedByName.trim(),
                                          date: needsAttentionBanner.date
                                            ? formatDate(
                                                needsAttentionBanner.date,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t("core:UNKNOWN"),
                                          summary:
                                            needsAttentionBanner.consolidatedSummary ??
                                            needsAttentionBanner.summaries.join(" "),
                                        }
                                      : {
                                          date: needsAttentionBanner.date
                                            ? formatDate(
                                                needsAttentionBanner.date,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t("core:UNKNOWN"),
                                          summary:
                                            needsAttentionBanner.consolidatedSummary ??
                                            needsAttentionBanner.summaries.join(" "),
                                        },
                                  )}
                                </Text>
                                {!needsAttentionBanner.consolidatedSummary && needsAttentionBanner.summaries.length > 2 && (
                                  <ul style={{ margin: "4px 0", paddingLeft: "20px", listStyleType: "disc" }}>
                                    {needsAttentionBanner.summaries.map((s, i) => (
                                      <li key={i}>
                                        <Text visual="bodyRegular" color="text.highlight.dark">
                                          {s}
                                        </Text>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {bannerExpanded && needsAttentionBanner.consolidatedSummary && needsAttentionBanner.summaries.length > 1 && (
                                  <ul style={{ margin: "4px 0", paddingLeft: "20px", listStyleType: "disc" }}>
                                    {needsAttentionBanner.summaries.map((s, i) => (
                                      <li key={i}>
                                        <Text visual="bodyRegular" color="text.highlight.dark">
                                          {s}
                                        </Text>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {bannerExpanded && (
                                  <Text visual="bodyRegular" color="text.highlight.dark">
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setBannerExpanded(false)}
                                      onKeyDown={(e) => { if (e.key === "Enter") setBannerExpanded(false); }}
                                      style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                                    >
                                      {t("directory:OPERATOR.FLYOUT.SEE_LESS")}
                                    </span>
                                  </Text>
                                )}
                              </Box>
                            : <Text visual="bodyRegular" color="text.highlight.dark">
                                {t(
                                  "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_FLAGGED",
                                  {
                                    date: needsAttentionBanner.date
                                      ? formatDate(
                                          needsAttentionBanner.date,
                                          DATE_PATTERN_STANDARD,
                                        )
                                      : t("core:UNKNOWN"),
                                  },
                                )}
                                {bannerExpanded && (
                                  <>
                                    {" "}
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setBannerExpanded(false)}
                                      onKeyDown={(e) => { if (e.key === "Enter") setBannerExpanded(false); }}
                                      style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                                    >
                                      {t("directory:OPERATOR.FLYOUT.SEE_LESS")}
                                    </span>
                                  </>
                                )}
                              </Text>}
                      </Box>
                      {bannerOverflows && !bannerExpanded && (
                        <Text
                          visual="bodyRegular"
                          color="text.highlight.dark"
                          style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                          onClick={() => setBannerExpanded(true)}
                        >
                          {t("directory:OPERATOR.FLYOUT.SEE_MORE")}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  {needsAttentionBanner.isStale ? (
                    <Flex gap="150" alignSelf="flex-end">
                      {operator?.claimed ? (
                        <Button
                          size="sm"
                          visual="primary"
                          mode="light"
                          disabled={addingToInviteQueue}
                          onClick={async () => {
                            if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                            try {
                              await addToInviteQueue({
                                variables: {
                                  operatorEntityId: String(operator._entityId),
                                  operatorName: getMultilingualString(operator.name),
                                  organizationId: organizationId!,
                                  inviteByEmailOnly: true,
                                  invitedEmail: operator.contact?.email ?? "",
                                  inviteStatus: "STALE",
                                },
                              });
                              await createPublicationStatus({
                                variables: {
                                  record: {
                                    entity: {
                                      _entityId: String(operator._entityId),
                                      _versionId: String(operator._versionId),
                                      type: EnumVersionedEntityReferenceType.Operator,
                                    },
                                    organization: organizationId,
                                    status: EnumPublicationStatusStatus.Approved,
                                  },
                                },
                              });
                              notifyDirectoryOperatorApproved();
                            } catch {
                              toastFactory.create({
                                title: t("core:ERROR.TITLE"),
                                description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                              });
                            }
                          }}
                        >
                          {t("directory:OPERATOR.FLYOUT.STALE_INVITE_TO_FRESHEN")}
                        </Button>
                      ) : (
                        <Button
                          variant="link"
                          mode="light"
                          size="sm"
                          onClick={handleEditListing}
                        >
                          {t("directory:OPERATOR.FLYOUT.EDIT_LISTING")}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        visual="accent.blue"
                        mode="dark"
                        disabled={isFlaggingNeedsAttention}
                        onClick={async () => {
                          if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                          try {
                            await createPublicationStatus({
                              variables: {
                                record: {
                                  entity: {
                                    _entityId: String(operator._entityId),
                                    _versionId: String(operator._versionId),
                                    type: EnumVersionedEntityReferenceType.Operator,
                                  },
                                  organization: organizationId,
                                  status: EnumPublicationStatusStatus.Approved,
                                },
                              },
                            });
                            notifyDirectoryOperatorApproved();
                          } catch {
                            toastFactory.create({
                              title: t("core:ERROR.TITLE"),
                              description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                            });
                          }
                        }}
                      >
                        {t("directory:OPERATOR.FLYOUT.STALE_SNOOZE")}
                      </Button>
                    </Flex>
                  ) : (
                    <Button
                      alignSelf="flex-end"
                      size="sm"
                      visual="accent.blue"
                      mode="dark"
                      disabled={isFlaggingNeedsAttention}
                      onClick={async () => {
                        if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                        try {
                          await createPublicationStatus({
                            variables: {
                              record: {
                                entity: {
                                  _entityId: String(operator._entityId),
                                  _versionId: String(operator._versionId),
                                  type: EnumVersionedEntityReferenceType.Operator,
                                },
                                organization: organizationId,
                                status: EnumPublicationStatusStatus.Approved,
                              },
                            },
                          });
                          notifyDirectoryOperatorApproved();
                        } catch {
                          toastFactory.create({
                            title: t("core:ERROR.TITLE"),
                            description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                          });
                        }
                      }}
                    >
                      {isFlaggingNeedsAttention ? (
                        <Spinner width="20px" height="20px" />
                      ) : (
                        t("directory:OPERATOR.FLYOUT.APPROVE_CHANGES")
                      )}
                    </Button>
                  )}
                </Flex>
              )}
              {/* Draft banner — shown for listings in draft/new status */}
              {!needsAttentionBanner && (!operator.status || operator.status === PublicationStatusType.Draft) && (
                <Flex
                  flexDir="column"
                  gap="200"
                  border="2px solid"
                  borderColor="border.highlight"
                  borderRadius="100"
                  bgColor="bg.highlight.lightest"
                  py="150"
                  px="200"
                >
                  <Flex flexDir="row" gap="200" alignItems="flex-start">
                    <Icon
                      Element={WarningTriangle}
                      w="24px"
                      h="24px"
                      color="icon.highlight.dark"
                      flexShrink={0}
                    />
                    <Flex flexDir="column" flex={1} minW={0} gap="100">
                      <Text visual="bodyRegular" color="text.highlight.dark">
                        {(operator.enrollments?.length ?? 0) > 1
                          ? listingUpkeepStrategySelected
                            ? t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CONNECTED_READY")
                            : t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CONNECTED")
                          : listingUpkeepStrategySelected
                            ? t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CREATED_READY")
                            : t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CREATED")}
                      </Text>
                    </Flex>
                  </Flex>
                  {listingUpkeepStrategySelected && (
                    <Button
                      visual="accent.blue"
                      mode="dark"
                      size="sm"
                      alignSelf="flex-end"
                      onClick={handleApproveListingClick}
                      disabled={isApprovingListing}
                    >
                      {isApprovingListing ? (
                        <Spinner width="20px" height="20px" />
                      ) : (
                        t("directory:OPERATOR.FLYOUT.MOVE_TO_DIRECTORY_NOW")
                      )}
                    </Button>
                  )}
                </Flex>
              )}
              <BackOfficeTags
                operator={operator}
                operatorEntityId={operatorId}
                organizationId={organizationId}
                onOperatorUpdated={onOperatorUpdated}
                compact
              />
              {/* CrowdRiff ID */}
              {crowdriffEnabled && (
                <BackOfficeCrowdRiffFlyoutSection
                  operatorEntityId={operatorId ?? undefined}
                  organizationId={organizationId}
                />
              )}

              {/* Listing Upkeep Strategy (with Staff panel) */}
              {(() => {
                const currentEnrollment = operator.enrollments?.find(
                  e => e?.organization?._id === organizationId,
                );
                const isDmoManaged =
                  currentEnrollment?.organizationControlled === true;
                const isClaimed = (operator as { claimed?: boolean })
                  .claimed === true;
                const allStaff = ((operator as { staff?: Array<{ _id: string; firstName: string; lastName: string; email?: string; status?: string; access?: { operators?: Array<{ entityId: unknown; role: string; status?: string; contactRoles?: string[] } | null> | null } }> }).staff ?? []);
                const opId = String(operator._entityId ?? "");
                const claimant = isClaimed
                  ? allStaff.find(m => {
                      const role = m.access?.operators?.find(o => o && String(o.entityId) === opId)?.role;
                      return role === "OWNER" || role === "EDITOR";
                    }) ?? allStaff[0]
                  : null;
                const claimantId = claimant ? String(claimant._id) : null;
                const isAlreadyActiveOrInvited = (m: { _id: string; access?: { operators?: Array<{ entityId: unknown; status?: string } | null> | null } }) => {
                  const entry = m.access?.operators?.find(o => o && String(o.entityId) === opId);
                  return entry?.status === EnumUserAccessOperatorsStatus.Accepted || entry?.status === EnumUserAccessOperatorsStatus.Invited;
                };
                const uninvitedStaffForBulk = isClaimed
                  ? allStaff.filter(m =>
                      !isAlreadyActiveOrInvited(m) &&
                      !invitedUserIds.has(String(m._id)) &&
                      String(m._id) !== claimantId,
                    )
                  : [];
                const showStrategySelector = !isClaimed;
                const hasChosenStrategy =
                  showStrategySelector && upkeepStrategy != null;
                const listingIsDraft =
                  !operator.status ||
                  operator.status === PublicationStatusType.Draft;
                return (
                  <Flex flexDir="column" gap="200">
                    <Flex alignItems="center" gap="150">
                      <Text visual="bodySemiBold">
                        {t(
                          "directory:OPERATOR.FLYOUT.LISTING_UPKEEP_STRATEGY",
                        )}
                      </Text>
                      {showStrategySelector &&
                        upkeepStrategy === null &&
                        !isDmoManaged &&
                        !listingIsDraft && (
                          <Tag
                            size="sm"
                            mode="light"
                            style={{
                              backgroundColor: "#e4e4e7",
                              color: "#52525b",
                            }}
                          >
                            {t(
                              "directory:OPERATOR.FLYOUT.UPKEEP_STRATEGY_TAG_NONE",
                            )}
                          </Tag>
                        )}
                      {isClaimed && (
                        <Tag size="sm" mode="light" style={{ backgroundColor: "#2563eb", color: "#ffffff" }}>
                          {t("directory:OPERATOR.FLYOUT.TAG_CLAIMED")}
                        </Tag>
                      )}
                      {isDmoManaged && !isClaimed && (
                        <Tag size="sm" mode="light" style={{ ...tagManagementStyle }}>
                          {`${orgShortName || "DMO"} Managed`}
                        </Tag>
                      )}
                    </Flex>
                    <Box
                      borderTopWidth="1px"
                      borderColor="border.light"
                      pt="200"
                    />
                    {/* Claimed: flat layout with staff table */}
                    {isClaimed && (
                      <Flex flexDir="column" gap="300">
                        {(() => {
                          const claimantName = claimant
                            ? [claimant.firstName, claimant.lastName].filter(Boolean).join(" ") || "—"
                            : null;
                          const claimantRole = claimant
                            ? claimant.access?.operators?.find(o => o && String(o.entityId) === opId)?.role ?? "STAFF"
                            : null;
                          return claimantName ? (
                            <Text visual="bodyRegular" color="text.mid">
                              {t("directory:OPERATOR.FLYOUT.UPKEEP_CLAIMED_BY", {
                                name: claimantName,
                                role: t(`core:LOOKUPS.OPERATOR_ROLE.${claimantRole}`),
                              })}
                            </Text>
                          ) : null;
                        })()}
                        {allStaff.length > 0 && (
                          <Flex flexDir="column" gap="150">
                            <table className={staffTableClass}>
                              <thead>
                                <tr>
                                  <th><Text visual="smallSemiBold">{t("directory:OPERATOR.FLYOUT.STAFF_TABLE_NAME")}</Text></th>
                                  <th><Text visual="smallSemiBold">{t("directory:OPERATOR.FLYOUT.STAFF_TABLE_ROLE")}</Text></th>
                                  <th><Text visual="smallSemiBold">{t("directory:OPERATOR.FLYOUT.STAFF_TABLE_STATUS")}</Text></th>
                                </tr>
                              </thead>
                              <tbody>
                                {allStaff.map(member => {
                                  const roleEntry = member.access?.operators?.find(
                                    op => op && String(op.entityId) === opId,
                                  );
                                  const role = roleEntry?.role ?? "STAFF";
                                  const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "—";
                                  const accessStatus = roleEntry?.status as EnumUserAccessOperatorsStatus | undefined;
                                  const enrollStatus = member.status as EnumUserStatus | undefined;
                                  const hasRole = !!roleEntry;
                                  const isViewOnly = role === "VIEW_ONLY";
                                  const isActive = accessStatus === EnumUserAccessOperatorsStatus.Accepted && enrollStatus === EnumUserStatus.Enrolled;
                                  const isInvited =
                                    accessStatus === EnumUserAccessOperatorsStatus.Invited ||
                                    (accessStatus === EnumUserAccessOperatorsStatus.Accepted && enrollStatus === EnumUserStatus.Provisioned) ||
                                    invitedUserIds.has(String(member._id));
                                  const isNotInvited = accessStatus === EnumUserAccessOperatorsStatus.NotInvited || (hasRole && !isActive && !isInvited);
                                  const statusLabel = isViewOnly
                                    ? t("directory:OPERATOR.FLYOUT.STAFF_STATUS_VIEW_ONLY")
                                    : isActive
                                      ? t("directory:OPERATOR.FLYOUT.STAFF_STATUS_ACTIVE")
                                      : isInvited
                                        ? t("directory:OPERATOR.FLYOUT.STAFF_STATUS_INVITED")
                                        : isNotInvited
                                          ? t("directory:OPERATOR.FLYOUT.STAFF_STATUS_NOT_INVITED")
                                          : "—";
                                  const statusColor = isViewOnly ? "text.mid" : isActive ? "text.success.dark" : isInvited ? "text.accent.magenta.mid" : "text.mid";
                                  const contactRoles = (roleEntry as { contactRoles?: string[] } | null)?.contactRoles ?? [];
                                  const roleLabel = contactRoles.includes("PRIMARY_CONTACT")
                                    ? tCore("STAFF.CONTACT_ROLES.PRIMARY_CONTACT")
                                    : contactRoles.includes("FINANCIAL_CONTACT")
                                      ? tCore("STAFF.CONTACT_ROLES.FINANCIAL_CONTACT")
                                      : t(`core:LOOKUPS.OPERATOR_ROLE.${role}`);
                                  return (
                                    <tr key={member._id}>
                                      <td><Text visual="smallRegular" color="text.mid">{name}</Text></td>
                                      <td><Text visual="smallRegular" color="text.mid">{roleLabel}</Text></td>
                                      <td><Text visual="smallRegular" color={statusColor}>{statusLabel}</Text></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {uninvitedStaffForBulk.length > 0 && (
                              <Button
                                mode="light"
                                size="sm"
                                visual="primary"
                                alignSelf="center"
                                disabled={addingAllToQueue || addingToInviteQueue}
                                onClick={async () => {
                                  if (!operator?._entityId || !organizationId) return;
                                  setAddingAllToQueue(true);
                                  const operatorName = getMultilingualString(operator.name);
                                  try {
                                    for (const member of uninvitedStaffForBulk) {
                                      const invitedUserName = [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email || "—";
                                      await addToInviteQueue({
                                        variables: {
                                          operatorEntityId: String(operator._entityId),
                                          operatorName,
                                          organizationId: String(organizationId),
                                          inviteByEmailOnly: false,
                                          invitedUserId: member._id,
                                          invitedUserName,
                                        } as any,
                                      });
                                    }
                                  } finally {
                                    setAddingAllToQueue(false);
                                  }
                                }}
                              >
                                {addingAllToQueue ? (
                                  <Spinner width="20px" height="20px" />
                                ) : (
                                  t("directory:OPERATOR.FLYOUT.INVITE_REMAINING_STAFF", { count: uninvitedStaffForBulk.length })
                                )}
                              </Button>
                            )}
                          </Flex>
                        )}
                      </Flex>
                    )}

                    {/* Unclaimed upkeep strategy — do-not-invite org variant */}
                    {showStrategySelector && operatorOnboardingDisabled && (() => {
                      const otherOrgManaging = operator.enrollments?.find(
                        e =>
                          e?.organization?._id !== organizationId &&
                          e?.organizationControlled === true,
                      );
                      return (
                        <Flex flexDir="column" gap="300">
                          <Text visual="bodyRegular" color="text.mid">
                            {t("directory:OPERATOR.FLYOUT.UPKEEP_NO_INVITE_ORG_MESSAGE")}
                          </Text>
                          {otherOrgManaging && (
                            <Flex
                              gap="200"
                              alignItems="center"
                              p="200"
                              border="1px solid"
                              borderColor="border.light"
                              borderRadius="100"
                            >
                              {otherOrgManaging.organization?.logo && (
                                <SmartImage
                                  image={otherOrgManaging.organization.logo}
                                  width={40}
                                  height={40}
                                  shape="rounded"
                                  isLogo
                                />
                              )}
                              <Text visual="bodyRegular" color="text.mid">
                                <Text as="span" visual="bodySemiBold">
                                  {getMultilingualString(
                                    otherOrgManaging.organization?.name ?? {},
                                  )}
                                </Text>
                                {t("directory:OPERATOR.FLYOUT.UPKEEP_OTHER_ORG_MANAGING_AFTER")}
                              </Text>
                            </Flex>
                          )}
                        </Flex>
                      );
                    })()}

                    {/* Unclaimed upkeep strategy — standard selector */}
                    {showStrategySelector && !operatorOnboardingDisabled && (() => {
                      const otherOrgManaging = operator.enrollments?.find(
                        e =>
                          e?.organization?._id !== organizationId &&
                          e?.organizationControlled === true,
                      );
                      const otherOrgName = otherOrgManaging
                        ? getMultilingualString(
                            otherOrgManaging.organization?.name ?? {},
                          )
                        : null;
                      const hasPendingOrSentClaimInvite = activeOperatorInviteEntries.some(
                        entry =>
                          entry.inviteStatus ===
                            EnumInviteQueueInviteStatus.Pending ||
                          entry.inviteStatus ===
                            EnumInviteQueueInviteStatus.Sent ||
                          entry.inviteStatus ===
                            EnumInviteQueueInviteStatus.Invited,
                      );

                      const isDraftListingForDefer =
                        !operator.status ||
                        operator.status === PublicationStatusType.Draft;

                      let businessOwnerInviteReveal:
                        | JSX.Element
                        | null = null;
                      let businessOwnerInviteActions:
                        | JSX.Element
                        | null = null;

                      if (upkeepStrategy === "businessOwner") {
                        const staffList =
                          (
                            operator as {
                              staff?: Array<{
                                _id: string;
                                firstName: string;
                                lastName: string;
                                email: string;
                                status?: string;
                                access?: {
                                  operators?: Array<{
                                    entityId: unknown;
                                    role: string;
                                    status?: string;
                                    contactRoles?: string[];
                                  } | null> | null;
                                };
                              }>;
                            }
                          ).staff ?? [];
                        const isDraftListing =
                          !operator.status ||
                          operator.status === PublicationStatusType.Draft;
                        const addToInviteQueueButtonLabel = isDraftListing
                          ? t(
                              "directory:OPERATOR.FLYOUT.ADD_TO_INVITE_QUEUE_AND_MOVE_TO_DIRECTORY",
                            )
                          : t("directory:OPERATOR.FLYOUT.ADD_TO_INVITE_QUEUE");
                        const hasStaff = staffList.length > 0;
                        const hasPublicEmail = !!operator.contact?.email;
                        const publicEmail = operator.contact?.email ?? "";
                        const canProceed = hasStaff || hasPublicEmail;
                        const opIdStr = String(operator._entityId ?? "");
                        const isEnrolled = (m: {
                          _id: string;
                          status?: string;
                          access?: {
                            operators?: Array<{
                              entityId: unknown;
                              status?: string;
                            } | null> | null;
                          };
                        }) => {
                          const opAccess = m.access?.operators?.find(
                            o =>
                              o && String(o.entityId) === opIdStr,
                          );
                          return (
                            opAccess?.status ===
                              EnumUserAccessOperatorsStatus.Accepted &&
                            m.status === EnumUserStatus.Enrolled
                          );
                        };
                        const invitedPeople = activeInviteEntries.map(entry => {
                          const staffMember = entry.invitedUser
                            ? staffList.find(
                                member =>
                                  String(member._id) ===
                                  String(entry.invitedUser),
                              )
                            : undefined;
                          const staffName = staffMember
                            ? [
                                staffMember.firstName,
                                staffMember.lastName,
                              ]
                                .filter(Boolean)
                                .join(" ")
                            : "";
                          return {
                            id: entry._id,
                            label:
                              staffName ||
                              entry.invitedUserName ||
                              entry.invitedEmail ||
                              "—",
                          };
                        });
                        const inviteCardEntry =
                          activeInviteEntries[0] ?? inviteQueueEntry;
                        const hasActiveNonCancelledInvite =
                          !!inviteCardEntry &&
                          inviteCardEntry.inviteStatus !==
                            EnumInviteQueueInviteStatus.Cancelled;

                        businessOwnerInviteReveal = (
                          <Flex flexDir="column" gap="200">
                            {!canProceed && (
                              <>
                                <Text
                                  visual="smallRegular"
                                  color="text.error.dark"
                                >
                                  {t(
                                    "directory:OPERATOR.FLYOUT.UPKEEP_INVITE_ADD_STAFF_OR_EMAIL",
                                  )}
                                </Text>
                                <LinkButton
                                  alignSelf="flex-start"
                                  type="button"
                                  onClick={() => {
                                    setSelectedInviteStaffUser(undefined);
                                    setIsInviteStaffModalOpen(true);
                                  }}
                                >
                                  {t("directory:OPERATOR.FLYOUT.ADD_STAFF")}
                                </LinkButton>
                              </>
                            )}
                            {hasPublicEmail && !hasStaff && (
                              <Flex flexDir="column" gap="150">
                                <Text
                                  visual="smallRegular"
                                  color="text.error.dark"
                                >
                                  {t(
                                    "directory:OPERATOR.FLYOUT.UPKEEP_INVITE_PUBLIC_EMAIL_WARNING",
                                    { email: publicEmail },
                                  )}
                                </Text>
                                <LinkButton
                                  alignSelf="flex-start"
                                  type="button"
                                  onClick={() => {
                                    setSelectedInviteStaffUser(undefined);
                                    setIsInviteStaffModalOpen(true);
                                  }}
                                >
                                  {t("directory:OPERATOR.FLYOUT.ADD_STAFF")}
                                </LinkButton>
                              </Flex>
                            )}
                            {hasStaff && (
                              <Flex flexDir="column" gap="150">
                                <Text visual="smallRegular" color="text.mid">
                                  {t(
                                    "directory:OPERATOR.FLYOUT.UPKEEP_SELECT_STAFF_TO_INVITE",
                                  )}
                                </Text>
                                <table className={staffTableClass}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: "32px" }} />
                                      <th>
                                        <Text visual="smallSemiBold">
                                          {t(
                                            "directory:OPERATOR.FLYOUT.STAFF_TABLE_NAME",
                                          )}
                                        </Text>
                                      </th>
                                      <th>
                                        <Text visual="smallSemiBold">
                                          {t(
                                            "directory:OPERATOR.FLYOUT.STAFF_TABLE_ROLE",
                                          )}
                                        </Text>
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {staffList.map(member => {
                                      const roleEntry =
                                        member.access?.operators?.find(
                                          op =>
                                            op &&
                                            String(op.entityId) === opIdStr,
                                        );
                                      const role = roleEntry?.role ?? "STAFF";
                                      const memberContactRoles = (roleEntry as { contactRoles?: string[] } | null)?.contactRoles ?? [];
                                      const memberRoleLabel = memberContactRoles.includes("PRIMARY_CONTACT")
                                        ? tCore("STAFF.CONTACT_ROLES.PRIMARY_CONTACT")
                                        : memberContactRoles.includes("FINANCIAL_CONTACT")
                                          ? tCore("STAFF.CONTACT_ROLES.FINANCIAL_CONTACT")
                                          : t(`core:LOOKUPS.OPERATOR_ROLE.${role}`);
                                      const name =
                                        [
                                          member.firstName,
                                          member.lastName,
                                        ]
                                          .filter(Boolean)
                                          .join(" ") || "—";
                                      const isChecked = selectedStaffIds.has(
                                        member._id,
                                      );
                                      const alreadyInvited =
                                        invitedUserIds.has(String(member._id));
                                      const enrolled = isEnrolled(member);
                                      return (
                                        <tr key={member._id}>
                                          <td>
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              disabled={
                                                alreadyInvited || enrolled
                                              }
                                              onChange={() => {
                                                setSelectedStaffIds(prev => {
                                                  const next = new Set(prev);
                                                  if (next.has(member._id))
                                                    next.delete(member._id);
                                                  else next.add(member._id);
                                                  return next;
                                                });
                                              }}
                                            />
                                          </td>
                                          <td>
                                            <Text
                                              visual="smallRegular"
                                              color="text.mid"
                                            >
                                              {name}
                                            </Text>
                                          </td>
                                          <td>
                                            <Text
                                              visual="smallRegular"
                                              color="text.mid"
                                            >
                                              {memberRoleLabel}
                                            </Text>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <LinkButton
                                  alignSelf="flex-end"
                                  type="button"
                                  onClick={() => {
                                    setSelectedInviteStaffUser(undefined);
                                    setIsInviteStaffModalOpen(true);
                                  }}
                                >
                                  {t("directory:OPERATOR.FLYOUT.ADD_STAFF")}
                                </LinkButton>
                              </Flex>
                            )}
                          </Flex>
                        );

                        businessOwnerInviteActions = (
                          <>
                            {hasStaff && selectedStaffIds.size > 0 ? (
                              <Button
                                visual="accent.blue"
                                mode="dark"
                                disabled={
                                  addingToInviteQueue || addingAllToQueue
                                }
                                onClick={async () => {
                                  if (
                                    addingToInviteQueue ||
                                    addingAllToQueue
                                  )
                                    return;
                                  if (
                                    !operator?._entityId ||
                                    !organizationId
                                  )
                                    return;
                                  setAddingAllToQueue(true);
                                  const operatorName =
                                    getMultilingualString(operator.name);
                                  try {
                                    for (const staffId of selectedStaffIds) {
                                      const member = staffList.find(
                                        m => m._id === staffId,
                                      );
                                      if (!member) continue;
                                      const invitedUserName =
                                        [
                                          member.firstName,
                                          member.lastName,
                                        ]
                                          .filter(Boolean)
                                          .join(" ") ||
                                        member.email ||
                                        "—";
                                      await addToInviteQueue({
                                        variables: {
                                          operatorEntityId: String(
                                            operator._entityId,
                                          ),
                                          operatorName,
                                          organizationId: String(
                                            organizationId,
                                          ),
                                          inviteByEmailOnly: false,
                                          invitedUserId: member._id,
                                          invitedUserName,
                                        } as any,
                                      });
                                    }
                                    setSelectedStaffIds(new Set());
                                    await Promise.all([
                                      refetchOperator(),
                                      refetchInviteQueue(),
                                      refetchAllInviteQueue(),
                                    ]);
                                  } finally {
                                    setAddingAllToQueue(false);
                                  }
                                }}
                              >
                                {addingAllToQueue ? (
                                  <Spinner width="20px" height="20px" />
                                ) : (
                                  addToInviteQueueButtonLabel
                                )}
                              </Button>
                            ) : !hasActiveNonCancelledInvite &&
                              !hasStaff &&
                              hasPublicEmail ? (
                              <Button
                                visual="accent.blue"
                                mode="dark"
                                disabled={addingToInviteQueue}
                                onClick={() => {
                                  if (addingToInviteQueue) return;
                                  if (
                                    !operator?._entityId ||
                                    !organizationId
                                  )
                                    return;
                                  addToInviteQueue({
                                    variables: {
                                      operatorEntityId: String(
                                        operator._entityId,
                                      ),
                                      operatorName: getMultilingualString(
                                        operator.name,
                                      ),
                                      organizationId: String(organizationId),
                                      inviteByEmailOnly: true,
                                      invitedEmail: operator.contact!.email!,
                                    } as any,
                                  });
                                }}
                              >
                                {addingToInviteQueue ? (
                                  <Spinner width="20px" height="20px" />
                                ) : (
                                  addToInviteQueueButtonLabel
                                )}
                              </Button>
                            ) : null}
                            {inviteCardEntry &&
                            inviteCardEntry.inviteStatus !==
                              EnumInviteQueueInviteStatus.Cancelled ? (
                              <Box className={listingDetailsCardClass}>
                                <Flex flexDir="column" gap="100">
                                  <Text visual="bodySemiBold">
                                    {t(
                                      "directory:OPERATOR.FLYOUT.INVITE_CARD_STATUS",
                                      {
                                        status: inviteCardEntry.inviteStatus
                                          .split("_")
                                          .map(
                                            w =>
                                              w.charAt(0) +
                                              w.slice(1).toLowerCase(),
                                          )
                                          .join(" "),
                                      },
                                    )}
                                  </Text>
                                  {invitedPeople.length > 0 && (
                                    <Flex flexDir="column" gap="050">
                                      {invitedPeople.map(person => (
                                        <Text
                                          key={person.id}
                                          visual="smallRegular"
                                          color="text.mid"
                                        >
                                          {person.label}
                                        </Text>
                                      ))}
                                    </Flex>
                                  )}
                                  <Text visual="smallRegular" color="text.mid">
                                    {t(
                                      "directory:OPERATOR.FLYOUT.INVITE_CARD_INITIATED_BY",
                                      {
                                        name: inviteCardEntry.initiatedByUserName,
                                        date: inviteCardEntry.initiatedDate
                                          ? formatDate(
                                              new Date(
                                                inviteCardEntry.initiatedDate,
                                              ),
                                              DATE_PATTERN_STANDARD,
                                            )
                                          : "—",
                                      },
                                    )}
                                  </Text>
                                  <Button
                                    visual="warning"
                                    mode="dark"
                                    size="sm"
                                    onClick={() =>
                                      cancelInviteQueue({
                                        variables: { _id: inviteCardEntry._id },
                                      })
                                    }
                                    disabled={cancellingInviteQueue}
                                  >
                                    {cancellingInviteQueue ? (
                                      <Spinner width="20px" height="20px" />
                                    ) : (
                                      t(
                                        "directory:OPERATOR.FLYOUT.INVITE_CARD_CANCEL",
                                      )
                                    )}
                                  </Button>
                                </Flex>
                              </Box>
                            ) : null}
                          </>
                        );
                      }

                      const deferLaterListingButton =
                        isDraftListingForDefer ? (
                          <Button
                            mode="light"
                            visual="primary"
                            w="full"
                            onClick={handleMoveToDirectoryDeferUpkeep}
                            disabled={isApprovingListing}
                          >
                            {isApprovingListing ? (
                              <Spinner width="20px" height="20px" />
                            ) : (
                              t(
                                "directory:OPERATOR.FLYOUT.UPKEEP_MOVE_TO_DIRECTORY_LATER",
                              )
                            )}
                          </Button>
                        ) : null;

                      return (
                      <Flex flexDir="column" gap="300">
                        <Text visual="bodyRegular" color="text.mid">
                          {t("directory:OPERATOR.FLYOUT.UPKEEP_PROMPT_NEITHER")}
                        </Text>
                        <Flex gap="150">
                          <Box flex={1}>
                            <ButtonCard
                              title={otherOrgManaging
                                ? t("directory:OPERATOR.FLYOUT.AN_ORGANIZATION")
                                : t("directory:OPERATOR.FLYOUT.MY_ORGANIZATION")}
                              description=""
                              onClick={() => setUpkeepStrategy("myOrganization")}
                              isSelected={upkeepStrategy === "myOrganization"}
                            />
                          </Box>
                          <Box flex={1} position="relative">
                            <ButtonCard
                              title={t("directory:OPERATOR.FLYOUT.BUSINESS_OWNER")}
                              description=""
                              onClick={() => setUpkeepStrategy("businessOwner")}
                              isSelected={upkeepStrategy === "businessOwner"}
                            />
                            {hasPendingOrSentClaimInvite && (
                              <Box
                                position="absolute"
                                top="150"
                                right="150"
                                w="10px"
                                h="10px"
                                borderRadius="full"
                                bg="#ea580c"
                                aria-hidden="true"
                              />
                            )}
                          </Box>
                        </Flex>
                        {upkeepStrategy === null && deferLaterListingButton}
                        {otherOrgManaging && upkeepStrategy === "myOrganization" && (
                          <Flex
                            gap="200"
                            alignItems="center"
                            p="200"
                            border="1px solid"
                            borderColor="border.light"
                            borderRadius="100"
                          >
                            {otherOrgManaging.organization?.logo && (
                              <SmartImage
                                image={otherOrgManaging.organization.logo}
                                width={40}
                                height={40}
                                shape="rounded"
                                isLogo
                              />
                            )}
                            <Text visual="bodyRegular" color="text.mid">
                              <Text as="span" visual="bodySemiBold">
                                {otherOrgName}
                              </Text>
                              {t("directory:OPERATOR.FLYOUT.UPKEEP_OTHER_ORG_MANAGING_DETAIL_AFTER")}
                            </Text>
                          </Flex>
                        )}
                        {hasChosenStrategy && upkeepStrategy === "businessOwner" && businessOwnerInviteReveal}
                        {hasChosenStrategy && upkeepStrategy === "myOrganization" && (() => {
                              const isDraft = !operator.status || operator.status === PublicationStatusType.Draft;
                              const buttonLabel = otherOrgManaging
                                ? t("directory:OPERATOR.FLYOUT.SAVE_AND_INCLUDE")
                                : isDraft
                                  ? t("directory:OPERATOR.FLYOUT.SAVE_AND_ACTIVATE")
                                  : t("directory:OPERATOR.FLYOUT.SAVE");
                              if (approveListingSuccess || isDmoManaged || (otherOrgManaging && !isDraft)) {
                                return (
                                  <Text visual="bodyRegular" color="text.mid">
                                    {otherOrgManaging && !isDmoManaged
                                      ? t("directory:OPERATOR.FLYOUT.INCLUDED_IN_DIRECTORY_SUCCESS")
                                      : t("directory:OPERATOR.FLYOUT.UPKEEP_DMO_RESPONSIBILITY")}{" "}
                                    {isDmoManaged && (
                                      <LinkButton
                                        visual="accent.blue"
                                        size="sm"
                                        onClick={handleUndoDmoManaged}
                                        disabled={isUndoingDmoManaged}
                                        style={{ display: "inline", verticalAlign: "baseline" }}
                                      >
                                        {isUndoingDmoManaged ? (
                                          <Spinner width="14px" height="14px" />
                                        ) : (
                                          t("directory:OPERATOR.FLYOUT.UNDO")
                                        )}
                                      </LinkButton>
                                    )}
                                  </Text>
                                );
                              }
                              return (
                                <Button
                                  visual="accent.blue"
                                  mode="dark"
                                  onClick={handleApproveListingClick}
                                  disabled={isApprovingListing}
                                >
                                  {isApprovingListing ? (
                                    <Spinner width="20px" height="20px" />
                                  ) : (
                                    buttonLabel
                                  )}
                                </Button>
                              );
                            })()}
                        {upkeepStrategy === "myOrganization" &&
                          deferLaterListingButton}
                        {hasChosenStrategy && upkeepStrategy === "businessOwner" && (
                          <Flex flexDir="column" gap="200">
                            {businessOwnerInviteActions}
                          </Flex>
                        )}
                        {upkeepStrategy === "businessOwner" && deferLaterListingButton}
                      </Flex>
                      );
                    })()}
                  </Flex>
                );
              })()}

              {/* Danger Zone */}
              <Flex flexDir="column" gap="200">
                <Text visual="bodySemiBold">
                  {t("directory:OPERATOR.FLYOUT.DANGER_ZONE")}
                </Text>
                <Box
                  borderTopWidth="1px"
                  borderColor="border.light"
                  pt="200"
                />
                <Flex direction="column" gap="200">
                  <Button
                    visual="warning"
                    mode="light"
                    size="sm"
                    onClick={() => setIsArchiveModalOpen(true)}
                  >
                    {isUnpublished
                      ? t("directory:OPERATOR.FLYOUT.REPUBLISH_PROFILE")
                      : t("directory:OPERATOR.FLYOUT.ARCHIVE_PROFILE")}
                  </Button>
                  <Button
                    visual="destructive"
                    mode="light"
                    size="sm"
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    {(operator.enrollments?.length ?? 0) > 1
                      ? t("directory:OPERATOR.FLYOUT.DISCONNECT_FROM_ORGANIZATION")
                      : t("directory:OPERATOR.FLYOUT.DELETE_PROFILE")}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
            )}

            {/* ── Quick View Tab ── */}
            {selectedView === VIEW_KEY.QUICK_VIEW && (
              <Flex
                flexDir="column"
                p="600"
                bg="white"
                gap="500"
              >
              {needsAttentionBanner && (
                <Flex
                  flexDir="column"
                  gap="200"
                  border="2px solid"
                  borderColor="border.highlight"
                  borderRadius="100"
                  bgColor="bg.highlight.lightest"
                  py="150"
                  px="200"
                >
                  <Flex flexDir="row" gap="200" alignItems="flex-start">
                    <Icon
                      Element={WarningTriangle}
                      w="24px"
                      h="24px"
                      color="icon.highlight.dark"
                      flexShrink={0}
                    />
                    <Flex flexDir="column" flex={1} minW={0} gap="100">
                      <Box
                        ref={bannerTextRef}
                        overflow="hidden"
                        style={
                          bannerExpanded
                            ? undefined
                            : {
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical" as const,
                              }
                        }
                      >
                        {needsAttentionBanner.isStale
                          ? <Text visual="bodyRegular" color="text.highlight.dark">
                              {needsAttentionBanner.summaries.join(" ")}
                            </Text>
                          : needsAttentionBanner.summaries.length > 0
                            ? <Box>
                                <Text visual="bodyRegular" color="text.highlight.dark">
                                  {t(
                                    getNeedsAttentionUpdatedBannerKey(
                                      needsAttentionBanner.consolidatedSummary,
                                      needsAttentionBanner.summaries.length,
                                      needsAttentionBanner.changedByName,
                                    ),
                                    needsAttentionBanner.changedByName?.trim()
                                      ? {
                                          name: needsAttentionBanner.changedByName.trim(),
                                          date: needsAttentionBanner.date
                                            ? formatDate(
                                                needsAttentionBanner.date,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t("core:UNKNOWN"),
                                          summary:
                                            needsAttentionBanner.consolidatedSummary ??
                                            needsAttentionBanner.summaries.join(" "),
                                        }
                                      : {
                                          date: needsAttentionBanner.date
                                            ? formatDate(
                                                needsAttentionBanner.date,
                                                DATE_PATTERN_STANDARD,
                                              )
                                            : t("core:UNKNOWN"),
                                          summary:
                                            needsAttentionBanner.consolidatedSummary ??
                                            needsAttentionBanner.summaries.join(" "),
                                        },
                                  )}
                                </Text>
                                {!needsAttentionBanner.consolidatedSummary && needsAttentionBanner.summaries.length > 2 && (
                                  <ul style={{ margin: "4px 0", paddingLeft: "20px", listStyleType: "disc" }}>
                                    {needsAttentionBanner.summaries.map((s, i) => (
                                      <li key={i}>
                                        <Text visual="bodyRegular" color="text.highlight.dark">
                                          {s}
                                        </Text>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {bannerExpanded && needsAttentionBanner.consolidatedSummary && needsAttentionBanner.summaries.length > 1 && (
                                  <ul style={{ margin: "4px 0", paddingLeft: "20px", listStyleType: "disc" }}>
                                    {needsAttentionBanner.summaries.map((s, i) => (
                                      <li key={i}>
                                        <Text visual="bodyRegular" color="text.highlight.dark">
                                          {s}
                                        </Text>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {bannerExpanded && (
                                  <Text visual="bodyRegular" color="text.highlight.dark">
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setBannerExpanded(false)}
                                      onKeyDown={(e) => { if (e.key === "Enter") setBannerExpanded(false); }}
                                      style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                                    >
                                      {t("directory:OPERATOR.FLYOUT.SEE_LESS")}
                                    </span>
                                  </Text>
                                )}
                              </Box>
                            : <Text visual="bodyRegular" color="text.highlight.dark">
                                {t(
                                  "directory:OPERATOR.FLYOUT.NEEDS_ATTENTION_BANNER_FLAGGED",
                                  {
                                    date: needsAttentionBanner.date
                                      ? formatDate(
                                          needsAttentionBanner.date,
                                          DATE_PATTERN_STANDARD,
                                        )
                                      : t("core:UNKNOWN"),
                                  },
                                )}
                                {bannerExpanded && (
                                  <>
                                    {" "}
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setBannerExpanded(false)}
                                      onKeyDown={(e) => { if (e.key === "Enter") setBannerExpanded(false); }}
                                      style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                                    >
                                      {t("directory:OPERATOR.FLYOUT.SEE_LESS")}
                                    </span>
                                  </>
                                )}
                              </Text>}
                      </Box>
                      {bannerOverflows && !bannerExpanded && (
                        <Text
                          visual="bodyRegular"
                          color="text.highlight.dark"
                          style={{ textDecoration: "underline", cursor: "pointer", opacity: 0.7 }}
                          onClick={() => setBannerExpanded(true)}
                        >
                          {t("directory:OPERATOR.FLYOUT.SEE_MORE")}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  {needsAttentionBanner.isStale ? (
                    <Flex gap="150" alignSelf="flex-end">
                      {operator?.claimed ? (
                        <Button
                          size="sm"
                          visual="primary"
                          mode="light"
                          disabled={addingToInviteQueue}
                          onClick={async () => {
                            if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                            try {
                              await addToInviteQueue({
                                variables: {
                                  operatorEntityId: String(operator._entityId),
                                  operatorName: getMultilingualString(operator.name),
                                  organizationId: organizationId!,
                                  inviteByEmailOnly: true,
                                  invitedEmail: operator.contact?.email ?? "",
                                  inviteStatus: "STALE",
                                },
                              });
                              await createPublicationStatus({
                                variables: {
                                  record: {
                                    entity: {
                                      _entityId: String(operator._entityId),
                                      _versionId: String(operator._versionId),
                                      type: EnumVersionedEntityReferenceType.Operator,
                                    },
                                    organization: organizationId,
                                    status: EnumPublicationStatusStatus.Approved,
                                  },
                                },
                              });
                              notifyDirectoryOperatorApproved();
                            } catch {
                              toastFactory.create({
                                title: t("core:ERROR.TITLE"),
                                description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                              });
                            }
                          }}
                        >
                          {t("directory:OPERATOR.FLYOUT.STALE_INVITE_TO_FRESHEN")}
                        </Button>
                      ) : (
                        <Button
                          variant="link"
                          mode="light"
                          size="sm"
                          onClick={handleEditListing}
                        >
                          {t("directory:OPERATOR.FLYOUT.EDIT_LISTING")}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        visual="accent.blue"
                        mode="dark"
                        disabled={isFlaggingNeedsAttention}
                        onClick={async () => {
                          if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                          try {
                            await createPublicationStatus({
                              variables: {
                                record: {
                                  entity: {
                                    _entityId: String(operator._entityId),
                                    _versionId: String(operator._versionId),
                                    type: EnumVersionedEntityReferenceType.Operator,
                                  },
                                  organization: organizationId,
                                  status: EnumPublicationStatusStatus.Approved,
                                },
                              },
                            });
                            notifyDirectoryOperatorApproved();
                          } catch {
                            toastFactory.create({
                              title: t("core:ERROR.TITLE"),
                              description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                            });
                          }
                        }}
                      >
                        {t("directory:OPERATOR.FLYOUT.STALE_SNOOZE")}
                      </Button>
                    </Flex>
                  ) : (
                    <Button
                      alignSelf="flex-end"
                      size="sm"
                      visual="accent.blue"
                      mode="dark"
                      disabled={isFlaggingNeedsAttention}
                      onClick={async () => {
                        if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                        try {
                          await createPublicationStatus({
                            variables: {
                              record: {
                                entity: {
                                  _entityId: String(operator._entityId),
                                  _versionId: String(operator._versionId),
                                  type: EnumVersionedEntityReferenceType.Operator,
                                },
                                organization: organizationId,
                                status: EnumPublicationStatusStatus.Approved,
                              },
                            },
                          });
                          notifyDirectoryOperatorApproved();
                        } catch {
                          toastFactory.create({
                            title: t("core:ERROR.TITLE"),
                            description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                          });
                        }
                      }}
                    >
                      {isFlaggingNeedsAttention ? (
                        <Spinner width="20px" height="20px" />
                      ) : (
                        t("directory:OPERATOR.FLYOUT.APPROVE_CHANGES")
                      )}
                    </Button>
                  )}
                </Flex>
              )}
              {/* Draft banner — shown for listings in draft/new status (Quick View) */}
              {!needsAttentionBanner && (!operator.status || operator.status === PublicationStatusType.Draft) && (
                <Flex
                  flexDir="column"
                  gap="200"
                  border="2px solid"
                  borderColor="border.highlight"
                  borderRadius="100"
                  bgColor="bg.highlight.lightest"
                  py="150"
                  px="200"
                >
                  <Flex flexDir="row" gap="200" alignItems="flex-start">
                    <Icon
                      Element={WarningTriangle}
                      w="24px"
                      h="24px"
                      color="icon.highlight.dark"
                      flexShrink={0}
                    />
                    <Flex flexDir="column" flex={1} minW={0} gap="100">
                      <Text visual="bodyRegular" color="text.highlight.dark">
                        {(operator.enrollments?.length ?? 0) > 1
                          ? listingUpkeepStrategySelected
                            ? t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CONNECTED_READY")
                            : t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CONNECTED")
                          : listingUpkeepStrategySelected
                            ? t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CREATED_READY")
                            : t("directory:OPERATOR.FLYOUT.DRAFT_BANNER_CREATED")}
                      </Text>
                    </Flex>
                  </Flex>
                  {listingUpkeepStrategySelected && (
                    <Button
                      visual="accent.blue"
                      mode="dark"
                      size="sm"
                      alignSelf="flex-end"
                      onClick={handleApproveListingClick}
                      disabled={isApprovingListing}
                    >
                      {isApprovingListing ? (
                        <Spinner width="20px" height="20px" />
                      ) : (
                        t("directory:OPERATOR.FLYOUT.MOVE_TO_DIRECTORY_NOW")
                      )}
                    </Button>
                  )}
                </Flex>
              )}

              {/* Tags */}
              <Box className={listingDetailsCardClass}>
                <Flex flexDir="column" gap="100">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.LISTING_TYPE")}
                  </Text>
                  {operator.type ? (
                    <span className={htmlContentProseClassName}>
                      {t(`core:LOOKUPS.OPERATOR_TYPE.${operator.type}`)}
                    </span>
                  ) : (
                    <span
                      className={cx(
                        htmlContentProseClassName,
                        flyoutQuickViewErrorValueClass,
                      )}
                    >
                      {t("directory:OPERATOR.FLYOUT.NONE_SELECTED")}
                    </span>
                  )}
                </Flex>

                <Flex flexDir="column" gap="100">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.TAGS_LABEL")}
                  </Text>
                  {operator.tags?.businessType &&
                  operator.tags.businessType.length > 0 ? (
                    <Flex flexWrap="wrap" gap="100">
                      {(() => {
                        const seen = new Set<string>();
                        return operator.tags.businessType.filter(tag => {
                          if (!tag) return false;
                          const name = getMultilingualString(tag.name);
                          if (seen.has(name)) return false;
                          seen.add(name);
                          return true;
                        }).map(
                          tag =>
                            tag && (
                              <Tag
                                key={tag._id}
                                size="sm"
                                style={tagBusinessStyle}
                              >
                                {getMultilingualString(tag.name)}
                              </Tag>
                            ),
                        );
                      })()}
                    </Flex>
                  ) : (
                    <span
                      className={cx(
                        htmlContentProseClassName,
                        flyoutQuickViewErrorValueClass,
                      )}
                    >
                      {t("directory:OPERATOR.FLYOUT.NONE_SELECTED")}
                    </span>
                  )}
                </Flex>

                <Flex flexDir="column" gap="100">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.KEYWORDS")}
                  </Text>
                  {operator.tags?.global && operator.tags.global.length > 0 ? (
                    <Flex flexWrap="wrap" gap="100">
                      {operator.tags.global.map(
                        tag =>
                          tag && (
                            <Tag
                              key={tag._id}
                              size="sm"
                              style={tagKeywordStyle}
                            >
                              {getMultilingualString(tag.name)}
                            </Tag>
                          ),
                      )}
                    </Flex>
                  ) : (
                    <span
                      className={cx(
                        htmlContentProseClassName,
                        flyoutQuickViewErrorValueClass,
                      )}
                    >
                      {t("directory:OPERATOR.FLYOUT.NONE_SELECTED")}
                    </span>
                  )}
                </Flex>

                {operator.type === "ACCOMMODATION" && (
                  <Flex flexDir="column" gap="100">
                    <Text visual="smallRegular" color="text.mid">
                      {t("directory:OPERATOR.FLYOUT.AMENITIES")}
                    </Text>
                    {operator.tags?.amenity && operator.tags.amenity.length > 0 ? (
                      <Flex flexWrap="wrap" gap="100">
                        {operator.tags.amenity.map(
                          tag =>
                            tag && (
                              <Tag
                                key={tag._id}
                                size="sm"
                                style={tagKeywordStyle}
                              >
                                {getMultilingualString(tag.name)}
                              </Tag>
                            ),
                        )}
                      </Flex>
                    ) : (
                      <span
                        className={cx(
                          htmlContentProseClassName,
                          flyoutQuickViewErrorValueClass,
                        )}
                      >
                        {t("directory:OPERATOR.FLYOUT.NONE_SELECTED")}
                      </span>
                    )}
                  </Flex>
                )}
              </Box>

              <Box
                width="100%"
                flexShrink={0}
                borderRadius="8px"
                overflow="hidden"
              >
                {flyoutGallerySection}
              </Box>

              {/* Contact + address — label above value per field (no section heading) */}
              <Flex flexDir="column" gap="400" alignItems="flex-start">
                <Flex flexDir="column" gap="100" alignItems="flex-start" width="100%">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.PUBLIC_EMAIL")}
                  </Text>
                  {operator.contact?.email ? (
                    <Box className={htmlContentProseClassName}>
                      <a href={`mailto:${operator.contact.email}`}>
                        {operator.contact.email}
                      </a>
                    </Box>
                  ) : (
                    <Box className={htmlContentProseClassName}>
                      <span className={flyoutQuickViewErrorValueClass}>
                        No public email
                      </span>
                    </Box>
                  )}
                </Flex>

                <Flex flexDir="column" gap="100" alignItems="flex-start" width="100%">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.PROFILE.KEY_INFO.PHONE")}
                  </Text>
                  {operator.contact?.phone ? (
                    <Box className={htmlContentProseClassName}>
                      <a href={phoneToTelHref(operator.contact.phone)}>
                        {operator.contact.phone}
                      </a>
                    </Box>
                  ) : (
                    <Box className={htmlContentProseClassName}>
                      <span className={flyoutQuickViewErrorValueClass}>
                        {t("directory:OPERATOR.FLYOUT.NO_PHONE")}
                      </span>
                    </Box>
                  )}
                </Flex>

                <Flex flexDir="column" gap="100" alignItems="flex-start" width="100%">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.WEBSITE")}
                  </Text>
                  {operator.contact?.links?.website ? (
                    <Box className={htmlContentProseClassName}>
                      <a
                        href={operator.contact.links.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {operator.contact.links.website.length > 75
                          ? `${operator.contact.links.website.slice(0, 75)}…`
                          : operator.contact.links.website}
                      </a>
                    </Box>
                  ) : (
                    <Box className={htmlContentProseClassName}>
                      <span className={flyoutQuickViewErrorValueClass}>
                        {t("directory:OPERATOR.FLYOUT.NO_WEBSITE")}
                      </span>
                    </Box>
                  )}
                </Flex>

                <Flex flexDir="column" gap="100" alignItems="flex-start" width="100%">
                  <Text visual="smallRegular" color="text.mid">
                    {t("directory:OPERATOR.FLYOUT.ADDRESS")}
                  </Text>
                  {operator.contact?.address ? (
                    <Box className={htmlContentProseClassName}>
                      <a
                        href={createGoogleMapsUrl(operator.contact.address)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {getAddressString(operator.contact.address)}
                      </a>
                    </Box>
                  ) : (
                    <Box className={htmlContentProseClassName}>
                      <span className={flyoutQuickViewErrorValueClass}>
                        {t("directory:OPERATOR.FLYOUT.NO_ADDRESS")}
                      </span>
                    </Box>
                  )}
                </Flex>
              </Flex>

              {/* Short description */}
              <Flex flexDir="column" gap="100" alignItems="flex-start">
                <Text visual="smallRegular" color="text.mid">
                  {t("directory:OPERATOR.FLYOUT.SHORT_DESCRIPTION")}
                </Text>
                {getMultilingualString(operator.shortDescription ?? {}) ? (
                  <Box
                    width="100%"
                    className={cx(
                      htmlContentProseClassName,
                      css({ whiteSpace: "pre-wrap" }),
                    )}
                  >
                    {getMultilingualString(operator.shortDescription ?? {})}
                  </Box>
                ) : (
                  <Box className={htmlContentProseClassName}>
                    <span className={flyoutQuickViewErrorValueClass}>
                      {t("directory:OPERATOR.FLYOUT.NO_SHORT_DESCRIPTION")}
                    </span>
                  </Box>
                )}
              </Flex>

              {/* Description */}
              <Flex flexDir="column" gap="100" alignItems="flex-start">
                <Text visual="smallRegular" color="text.mid">
                  {t("directory:OPERATOR.FLYOUT.DESCRIPTION")}
                </Text>
                {getMultilingualString(operator.description ?? {}) ? (
                  <Box width="100%">
                    <HtmlContent
                      html={getMultilingualString(operator.description ?? {}) ?? ""}
                    />
                  </Box>
                ) : (
                  <Box className={htmlContentProseClassName}>
                    <span className={flyoutQuickViewErrorValueClass}>
                      {t("directory:OPERATOR.FLYOUT.NO_DESCRIPTION")}
                    </span>
                  </Box>
                )}
              </Flex>

              {/* Operating Hours */}
              <Flex flexDir="column" gap="100">
                <Text visual="smallRegular" color="text.mid">
                  {t("directory:OPERATOR.FLYOUT.OPERATING_HOURS_TITLE")}
                </Text>
                {hasValidHours ? (
                  <Box borderRadius="8px" overflow="hidden">
                    <HoursOfOperation
                      data={
                        operator.hoursOfOperation as Parameters<
                          typeof HoursOfOperation
                        >[0]["data"]
                      }
                      dayLabelKeyPrefix="core:LOOKUPS.DAYS"
                      closedLabelKey="directory:OPERATOR.FLYOUT.OPERATING_HOURS_CLOSED"
                      className={flyoutHoursTableClass}
                    />
                  </Box>
                ) : (
                  <Box className={htmlContentProseClassName}>
                    <span className={flyoutQuickViewErrorValueClass}>
                      {t("directory:OPERATOR.FLYOUT.OPERATING_HOURS_NONE")}
                    </span>
                  </Box>
                )}
              </Flex>

              {/* Staff (read-only) */}
              <Flex flexDir="column" gap="100">
                <Text visual="smallRegular" color="text.mid">
                  {t("directory:OPERATOR.FLYOUT.ASSOCIATED_MEMBERS")}
                </Text>
                <table className={staffTableClass}>
                  <thead>
                    <tr>
                      <th>{t("directory:OPERATOR.FLYOUT.STAFF_TABLE_NAME")}</th>
                      <th>{t("directory:OPERATOR.FLYOUT.STAFF_TABLE_ROLE")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const staff = (operator as { staff?: Array<{ _id: string; firstName: string; lastName: string; access?: { operators?: Array<{ entityId: unknown; role: string; contactRoles?: string[] } | null> | null } }> }).staff;
                      const operatorEntityIdStr = String(operator._entityId ?? "");
                      if (!staff?.length) {
                        return (
                          <tr>
                            <td colSpan={2}>
                              <span
                                className={cx(
                                  htmlContentProseClassName,
                                  flyoutQuickViewErrorValueClass,
                                )}
                              >
                                {t("directory:OPERATOR.FLYOUT.NO_STAFF")}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      return staff.map(member => {
                        const roleEntry = member.access?.operators?.find(
                          op => op && String(op.entityId) === operatorEntityIdStr,
                        );
                        const role = roleEntry?.role ?? "STAFF";
                        const memberContactRoles = roleEntry?.contactRoles ?? [];
                        const memberRoleLabel = memberContactRoles.includes("PRIMARY_CONTACT")
                          ? tCore("STAFF.CONTACT_ROLES.PRIMARY_CONTACT")
                          : memberContactRoles.includes("FINANCIAL_CONTACT")
                            ? tCore("STAFF.CONTACT_ROLES.FINANCIAL_CONTACT")
                            : t(`core:LOOKUPS.OPERATOR_ROLE.${role}`);
                        const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "—";
                        return (
                          <tr key={member._id}>
                            <td>
                              <span className={htmlContentProseClassName}>
                                {name}
                              </span>
                            </td>
                            <td>
                              <span className={htmlContentProseClassName}>
                                {memberRoleLabel}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </Flex>

              {/* Action button — varies by publication status */}
              {(operator.status === PublicationStatusType.Draft ||
                operator.status === PublicationStatusType.NeedsApproval) && (
                <Button
                  visual="accent.blue"
                  mode="dark"
                  disabled={isApprovingListing}
                  onClick={async () => {
                    if (!operator?._entityId || !operator?._versionId || !organizationId) return;
                    setIsApprovingListing(true);
                    try {
                      await createPublicationStatus({
                        variables: {
                          record: {
                            entity: {
                              _entityId: String(operator._entityId),
                              _versionId: String(operator._versionId),
                              type: EnumVersionedEntityReferenceType.Operator,
                            },
                            organization: organizationId,
                            status: EnumPublicationStatusStatus.Approved,
                          },
                        },
                      });
                      notifyDirectoryOperatorApproved();
                    } catch {
                      toastFactory.create({
                        title: t("core:ERROR.TITLE"),
                        description: t("directory:OPERATOR.ERROR.UPDATING_OPERATOR"),
                      });
                    } finally {
                      setIsApprovingListing(false);
                    }
                  }}
                >
                  {isApprovingListing ? (
                    <Spinner width="20px" height="20px" />
                  ) : operator.status === PublicationStatusType.NeedsApproval ? (
                    t("directory:OPERATOR.FLYOUT.APPROVE_LISTING_AS_IS")
                  ) : (
                    t("directory:OPERATOR.FLYOUT.ACTIVATE_LISTING")
                  )}
                </Button>
              )}
              {operator.status === PublicationStatusType.Unpublished && (
                <Button
                  visual="accent.blue"
                  mode="dark"
                  onClick={() => setIsArchiveModalOpen(true)}
                >
                  {t("directory:OPERATOR.FLYOUT.REOPEN_BUSINESS")}
                </Button>
              )}
            </Flex>
            )}
            </Box>
          </Flex>

          {/* Sticky footer: Edit Listing + Previous | Next */}
          {(navIds.length > 1 || placement !== "leftPane") && (
            <Flex
              px="500"
              py="300"
              borderTopWidth="1px"
              borderColor="border.light"
              borderStyle="solid"
              bg="white"
              boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.10)"
              justifyContent="space-between"
              alignItems="center"
              gap="200"
              flexShrink={0}
            >
              {navIds.length > 1 ? (
                <>
                  <Button
                    variant="link"
                    mode="light"
                    size="sm"
                    onClick={goPrev}
                    disabled={!hasPrev}
                  >
                    ← {t("directory:OPERATOR.FLYOUT.PREVIOUS_LISTING")}
                  </Button>
                  {placement !== "leftPane" && (
                    <Button
                      variant="link"
                      mode="light"
                      size="sm"
                      onClick={handleEditListing}
                    >
                      {t("directory:OPERATOR.FLYOUT.EDIT_LISTING")}
                    </Button>
                  )}
                  <Button
                    variant="link"
                    mode="light"
                    size="sm"
                    onClick={goNext}
                    disabled={!hasNext}
                  >
                    {t("directory:OPERATOR.FLYOUT.NEXT_LISTING")} →
                  </Button>
                </>
              ) : (
                <Flex justifyContent="flex-end" w="full">
                  <Button
                    variant="link"
                    mode="light"
                    size="sm"
                    onClick={handleEditListing}
                  >
                    {t("directory:OPERATOR.FLYOUT.EDIT_LISTING")}
                  </Button>
                </Flex>
              )}
            </Flex>
          )}
        </Grid>
      )}
      {operator?._entityId != null && (
        <PeopleContextProvider NOP>
          <PeopleTagsContextProvider>
            <InviteStaffModal
              _entityId={String(operator._entityId)}
              type={EnumEntityReferenceType.Operator}
              isOpen={isInviteStaffModalOpen}
              isMaxZIndex
              onClose={handleInviteStaffModalClose}
              setUpdatingEmail={() => {}}
              refetch={refetchOperator as unknown as UseInviteStaffProps["refetch"]}
              isForOrganization={false}
              user={selectedInviteStaffUser}
              onViewFullProfileClick={() => {
                setIsInviteStaffModalOpen(false);
                setSelectedInviteStaffUser(undefined);
                setPersonDetailsId(selectedInviteStaffUser?.personId ?? null);
                setIsPersonDetailsModalOpen(true);
              }}
              viewOnly={false}
              onInviteComplete={refreshInviteStaffState}
            />
            <PersonDetailsModal
              isOpen={isPersonDetailsModalOpen}
              setIsOpen={setIsPersonDetailsModalOpen}
              personId={personDetailsId}
              setPersonId={setPersonDetailsId}
            />
          </PeopleTagsContextProvider>
        </PeopleContextProvider>
      )}
      {/* Archive / Republish confirmation */}
      <ConfirmInputModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        title={t(
          `directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.PUBLISHING_STATUS.${isUnpublished ? "REPUBLISH" : "UNPUBLISH"}.TITLE`,
        )}
        HeaderIcon={EyeClosed}
        cancelButtonProps={{ onClick: () => setIsArchiveModalOpen(false) }}
        saveButtonCopy={t(
          `directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.PUBLISHING_STATUS.${isUnpublished ? "REPUBLISH" : "UNPUBLISH"}.BUTTON_TEXT`,
        )}
        saveButtonProps={{
          visual: isUnpublished ? "success" : "warning",
          isLoading: loadingArchive,
          onClick: () => handleArchiveOrRepublish(!isUnpublished),
        }}
        textToConfirm={t("core:CONFIRMATION_MODAL.I_CONFIRM")}
        isMaxZIndex
      >
        <Text color="text.mid">
          {t(
            `directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.PUBLISHING_STATUS.${isUnpublished ? "REPUBLISH" : "UNPUBLISH"}.MODAL.DESCRIPTION`,
            { date: formatDate(operator?.updatedDateISO) },
          )}
        </Text>
      </ConfirmInputModal>

      {/* Delete / Disconnect confirmation */}
      <ConfirmInputModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t(
          (operator?.enrollments?.length ?? 0) > 1
            ? "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DISCONNECT.TITLE"
            : "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DELETE.TITLE",
        )}
        HeaderIcon={Trash}
        cancelButtonProps={{ onClick: () => setIsDeleteModalOpen(false) }}
        saveButtonCopy={t(
          (operator?.enrollments?.length ?? 0) > 1
            ? "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DISCONNECT.BUTTON_TEXT"
            : "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DELETE.BUTTON_TEXT",
        )}
        saveButtonProps={{
          visual: "destructive",
          isLoading: loadingDelete,
          onClick: handleDeleteOperator,
        }}
        textToConfirm={t("core:CONFIRMATION_MODAL.I_CONFIRM")}
        isMaxZIndex
      >
        <Text color="text.mid">
          {t(
            (operator?.enrollments?.length ?? 0) > 1
              ? "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DISCONNECT.DESCRIPTION"
              : "directory:OPERATOR.BACK_OFFICE.VISIBILITY_STATUS.DELETE.DESCRIPTION",
          )}
        </Text>
      </ConfirmInputModal>

      <ConfirmInputModal
        isOpen={isMarkDeletedModalOpen}
        onClose={() => setIsMarkDeletedModalOpen(false)}
        title={tAdminlandListing("MARK_DELETED_MODAL_TITLE")}
        HeaderIcon={WarningTriangle}
        cancelButtonProps={{
          onClick: () => setIsMarkDeletedModalOpen(false),
        }}
        saveButtonCopy={tAdminlandListing("MARK_DELETED_MODAL_CONFIRM")}
        saveButtonProps={{
          visual: "warning",
          isLoading: loadingMarkDeletedByInternal,
          onClick: handleMarkDeletedByInternal,
        }}
        textToConfirm={tAdminlandListing("MARK_DELETED_CONFIRM_PHRASE")}
        label={tAdminlandListing("MARK_DELETED_MODAL_LABEL")}
        isMaxZIndex
      >
        <Text color="text.mid">
          {tAdminlandListing("MARK_DELETED_MODAL_DESCRIPTION")}
        </Text>
      </ConfirmInputModal>

      <ConfirmInputModal
        isOpen={isDeleteCompletelyModalOpen}
        onClose={() => setIsDeleteCompletelyModalOpen(false)}
        title={tAdminlandListing("DELETE_COMPLETELY_MODAL_TITLE")}
        HeaderIcon={Trash}
        cancelButtonProps={{
          onClick: () => setIsDeleteCompletelyModalOpen(false),
        }}
        saveButtonCopy={tAdminlandListing("DELETE_COMPLETELY_MODAL_CONFIRM")}
        saveButtonProps={{
          visual: "destructive",
          isLoading: loadingDeleteCompletelyByInternal,
          onClick: handleDeleteCompletelyByInternal,
        }}
        textToConfirm={tAdminlandListing("DELETE_COMPLETELY_CONFIRM_PHRASE")}
        label={tAdminlandListing("DELETE_COMPLETELY_MODAL_LABEL")}
        isMaxZIndex
      >
        <Text color="text.mid">
          {tAdminlandListing("DELETE_COMPLETELY_MODAL_DESCRIPTION")}
        </Text>
      </ConfirmInputModal>
    </Modal>
    </>
  );
};
